import {
  JSX,
  Show,
  For,
  createSignal,
  onMount,
  createEffect,
  createMemo,
  on,
} from 'solid-js';
import { createForm } from '@tanstack/solid-form';
import {
  IssueRightPanelProps,
  IReference,
  I3DActionType,
  I3DActionRequest,
  I3DActionResponse,
  IMarker,
  type HideableElement,
  type IFeatureConfig,
  type IIssueViewPreviewData,
} from '../../types/issue.types';
import {
  getProjectDetails,
  createaNewFolder,
  CreateaNewFolderData,
  listItemsinaFolder,
  postAddattachments,
  type CreateViewRequest,
  type ProjectDetailsResponse,
} from '@data-access/connect/tc-tcps';
import * as CDM from 'trimble-connect-sdk';
import { ServiceResponse, FileEntry } from 'trimble-connect-sdk';
import { AttachmentPicker } from '@tc-attachment-picker/ui';
import {
  CommentContainer,
  AddComment,
  CommentProvider,
  IComment,
  ICommentData,
} from '@tc-comments/ui';
import { createClient } from '@hey-api/client-fetch';
import { QueryClient, useQueryClient } from '@tanstack/solid-query';
import {
  ITagItem,
  Separators,
  defineSolidConfirmPopup,
  defineSolidSpinner,
} from '@tcweb/connect-ui';
import { initWebComponents } from '../../init-web-components';
import { VIEW_THUMBNAIL } from '../../constants/thumbnail';
import {
  buildProjectSight360ViewerDeepLink,
  isMayaReservedViewData,
  type MayaReservedViewData,
  useApiServices,
  useConfiguration,
  useProjectRegion,
} from '@trimble/tc-issue-management-providers';
import {
  ViewThumbnail,
  RightPanelOverlay,
  useSnackbar,
  IssueSharingModal,
} from '../../components';
import {
  getUserName,
  getUserNameByTiduuid,
  buildAuthImg,
} from '../../utils/util';
import {
  resolveTypeIconDisplay,
  getPriorityColor,
  getStatusColor,
  getStatusTextColor,
  getTypeTranslation,
  getPriorityTranslation,
  getStatusTranslation,
} from '../../utils/issue-icons';
import {
  convertMMDDYYYYtoISO,
  formatDateToMMDDYYYY,
  parseDateForDisplay,
} from '../../utils';
import { IssueExport, ITFilterData } from '../IssueExport/IssueExport';
import '../IssueExport/IssueExport.css';
import './IssueRightPanel.css';
import { translate } from '@fulcrum/lib';
import {
  type IssueResponse,
  getIssuePermissions,
} from '@data-access/connect/tc-issue-management';
import type { IssueAclMap } from '../../types/issue-permissions.types';
import {
  buildSharedWithChips,
  canDeleteIssueFromActions,
  canUpdatePermissionsFromActions,
  grantReadAccessOnFollowForIssue,
  parseExplicitIssueAcl,
} from '../../utils/issue-permissions.util';

type ITabViewMode = 'COMMENT' | 'VIEW' | 'FILE' | 'FOR3D';
export const OPEN_IN_360_VIEWER_KEY = 'TC.Topic.Open360Viewer';
export const VIEW_360_KEY = 'TC.Topic.View360';

const getOpenIn360ViewerLabel = (): string => translate(OPEN_IN_360_VIEWER_KEY);
const getView360Label = (): string => translate(VIEW_360_KEY);
const EMBEDDED_VIEW_360_BADGE_SRC = new URL(
  '../../../images/embedded-view-360.svg',
  import.meta.url
).href;

export function getIssueViewpointDropDownOptions(
  isLoadOriginalVersionsHidden: boolean,
  hasMayaReservedViewData: boolean
): string[] {
  const options = isLoadOriginalVersionsHidden
    ? [translate('TC.Common.Preview')]
    : [
        translate('TC.Common.Preview'),
        translate('TC.Views.LoadWithSpecificVersions'),
      ];

  if (hasMayaReservedViewData) {
    options.push(getOpenIn360ViewerLabel());
  }

  return options;
}

export function shouldRedirectMayaReservedViewToProjectSight(
  featureConfig?: IFeatureConfig
): boolean {
  return (
    featureConfig?.mayaReservedViewRouting?.redirectToProjectSight !== false
  );
}

export function shouldShowMayaReservedViewRoutingAction(
  hasMayaReservedViewData: boolean,
  featureConfig?: IFeatureConfig
): boolean {
  return (
    hasMayaReservedViewData &&
    shouldRedirectMayaReservedViewToProjectSight(featureConfig)
  );
}

export function IssueRightPanel(props: IssueRightPanelProps): JSX.Element {
  // Initialize snackbar hook for testing
  const { showSnackbar } = useSnackbar();

  const [tabPort, setTabPort] = createSignal<ITabViewMode>('VIEW');
  const [showSharingModal, setShowSharingModal] = createSignal(false);
  const [sharedWithChips, setSharedWithChips] = createSignal<string[]>([]);
  const [isSharedWithLoaded, setIsSharedWithLoaded] = createSignal(false);

  const isElementHidden = (id: HideableElement): boolean =>
    props.featureConfig?.hiddenElements?.includes(id) ?? false;

  const hiddenResourceIds = (): readonly string[] =>
    props.featureConfig?.references?.hiddenResourceIds ?? [];
  const shouldRedirectMayaViewToProjectSight = (): boolean =>
    shouldRedirectMayaReservedViewToProjectSight(props.featureConfig);

  // If a hidden tab is currently active, fall back to the VIEW tab
  createEffect(() => {
    if (isElementHidden('marker-tab') && tabPort() === 'FOR3D') {
      setTabPort('VIEW');
    }
    if (isElementHidden('references-tab') && tabPort() === 'FILE') {
      setTabPort('VIEW');
    }
  });

  const [showExportDialog, setShowExportDialog] = createSignal(false);
  const [searchItems, setSearchItems] = createSignal<ITagItem[]>([]);
  const [isAssignedToMe, setIsAssignedToMe] = createSignal(false);
  const [filterData, setFilterData] = createSignal<ITFilterData>({
    types: [],
    status: [],
    priority: [],
    tags: [],
  });
  const [coordinationCollapse, setCoordinationCollapse] = createSignal(false);
  const [commentsCount, setCommentsCount] = createSignal(0);
  const DEFAULT_VIEWPOINT_COUNT = 2;
  const VIEWPOINT_ITEM_LIMIT = 3;
  const VIEWPOINT_CLASSES_COUNT = 3;
  const DECIMAL_PLACES = 2;
  // No-op function for refreshViewpointFn when not needed
  const noOpRefreshViewpointFn = (): void => {
    // Intentionally empty - viewpoint refresh not needed in this context
  };
  const [viewpointCount, setViewpointCount] = createSignal(
    DEFAULT_VIEWPOINT_COUNT
  ); // Updated to match mock data
  const [referenceCount, setReferenceCount] = createSignal(0);

  // Comment-related state
  const [showAddComment, setShowAddComment] = createSignal(false);
  const [commentData, setCommentData] = createSignal<ICommentData | undefined>(
    undefined
  );

  // Reference-related state for create/edit form
  const [formReferences, setFormReferences] = createSignal<IReference[]>([]);
  const [showFormAttachmentPicker, setShowFormAttachmentPicker] =
    createSignal(false);

  // Preview overlay state
  const [isPreviewOn, setIsPreviewOn] = createSignal(false);
  const [previewIndex, setPreviewIndex] = createSignal(0);
  const [previewMode, setPreviewMode] = createSignal<'VIEW' | 'SLIDEVIEW'>(
    'VIEW'
  );

  // 3D Action state management
  const [threeDActionLoading, setThreeDActionLoading] =
    createSignal<I3DActionType | null>(null);
  const [_threeDActionResult, setThreeDActionResult] =
    createSignal<I3DActionResponse | null>(null);

  // Marker state management
  const [currentMarker, setCurrentMarker] = createSignal<IMarker | null>(null);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = createSignal(false);
  const [deleteInProgress, setDeleteInProgress] = createSignal(false);

  // Thumbnail blob URL state
  const [thumbnailBlobUrl, setThumbnailBlobUrl] = createSignal<string>('');

  // Issue view preview state (opt-in via featureConfig.issueViewPreview)
  const [issueViewPreviewData, setIssueViewPreviewData] =
    createSignal<IIssueViewPreviewData | null>(null);
  const [reservedViewDataMap, setReservedViewDataMap] = createSignal<
    Record<string, MayaReservedViewData>
  >({});
  const [isReservedViewDataLoading, setIsReservedViewDataLoading] =
    createSignal(false);

  // Marker icon constant
  const markerIcon = createMemo(
    () =>
      props.featureConfig?.marker?.markerIcon ??
      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjUiIHZpZXdCb3g9IjAgMCAyNCAyNSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0xNyAzLjVWMTIuNUMxNyAxMy4wNSAxNi41NSAxMy41IDE2IDEzLjVINkwyIDE3LjVWMy41QzIgMi45NSAyLjQ1IDIuNSAzIDIuNUgxNkMxNi41NSAyLjUgMTcgMi45NSAxNyAzLjVaTTE5IDYuNUgyMUMyMS41NSA2LjUgMjIgNi45NSAyMiA3LjVWMjIuNUwxOCAxOC41SDdDNi40NSAxOC41IDYgMTguMDUgNiAxNy41VjE1LjVIMTlWNi41WiIgZmlsbD0iIzBENkFBOCIvPgo8L3N2Zz4='
  );

  // API services for mutations
  const apiServices = useApiServices();
  const referenceApiService = apiServices.issueReferenceApi;
  const fileApiService = apiServices.fileApi;

  // Query to get all views in project for the selected issue
  // Uses a single reactive query that automatically refetches when the issue changes
  // eslint-disable-next-line solid/reactivity -- Accessor is tracked internally by TanStack Query's useQuery
  const viewsQuery = apiServices.viewApi.listAllViewsInProjectQuery(() => {
    if (!props.projectId || !props.selectedIssue?.id || !props.contextId) {
      return undefined;
    }
    return {
      query: {
        projectId: props.projectId,
        contextId: props.contextId,
        issueId: props.selectedIssue.id,
        detail: false, // Use minimal details for performance
      },
    };
  });

  // Transform API data to UI format for overlay
  const overlayViewpointList = createMemo(() => {
    if (viewsQuery.isLoading || viewsQuery.isError || !viewsQuery.data) {
      return [];
    }

    return viewsQuery.data.map((view) => ({
      // eslint-disable-next-line @typescript-eslint/naming-convention -- API response property name
      view_id: view.id,
      guid: view.id,
      name: view.name,
      thumbnail: view.thumbnail,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- API response property name
      created_at: view.createdOn,
      author: view.createdBy
        ? `${view.createdBy.firstName} ${view.createdBy.lastName}`
        : 'Unknown',
    }));
  });

  const firstViewpointShows360Action = createMemo(() => {
    const firstViewpoint = overlayViewpointList()[0];
    return shouldShowMayaReservedViewRoutingAction(
      Boolean(firstViewpoint && reservedViewDataMap()[firstViewpoint.guid]),
      props.featureConfig
    );
  });

  const fetchMayaReservedViewData = async (
    viewIds: readonly string[]
  ): Promise<void> => {
    if (viewIds.length === 0) {
      setReservedViewDataMap({});
      return;
    }

    setIsReservedViewDataLoading(true);
    const results = await Promise.allSettled(
      viewIds.map((viewId) =>
        apiServices.viewApi.getReservedViewData(viewId, 'maya')
      )
    );
    const nextReservedViewDataMap: Record<string, MayaReservedViewData> = {};

    results.forEach((result, index) => {
      if (
        result.status === 'fulfilled' &&
        isMayaReservedViewData(result.value)
      ) {
        nextReservedViewDataMap[viewIds[index]] = result.value;
      }
    });

    setReservedViewDataMap(nextReservedViewDataMap);
    setIsReservedViewDataLoading(false);
  };

  createEffect(
    on(
      () => {
        if (viewsQuery.isLoading || viewsQuery.isError || !viewsQuery.data) {
          return '';
        }
        return viewsQuery.data.map((view) => view.id).join('|');
      },
      (viewIdsKey) => {
        const viewIds = viewIdsKey
          .split('|')
          .filter((viewId): viewId is string => viewId.length > 0);

        if (viewIds.length === 0) {
          setReservedViewDataMap({});
          return;
        }

        void fetchMayaReservedViewData(viewIds);
      }
    )
  );

  // Compute preview thumbnail - use first available view thumbnail or placeholder
  const previewThumbnail = createMemo(() => {
    const viewpoints = overlayViewpointList();
    // Return first viewpoint's thumbnail if available, otherwise return placeholder
    if (viewpoints.length > 0 && viewpoints[0].thumbnail) {
      return viewpoints[0].thumbnail;
    }
    return VIEW_THUMBNAIL;
  });

  // Fetch thumbnail as blob when preview thumbnail changes
  createEffect(() => {
    const thumbUrl = previewThumbnail();
    if (thumbUrl && thumbUrl !== VIEW_THUMBNAIL) {
      buildAuthImg(thumbUrl, props.accessToken)
        .then((blobUrl) => {
          setThumbnailBlobUrl(blobUrl);
        })
        .catch((error) => {
          console.error('Error fetching issue thumbnail:', error);
          setThumbnailBlobUrl('');
        });
    } else {
      setThumbnailBlobUrl('');
    }
  });

  // Configuration and region services for URL construction
  const configuration = useConfiguration();
  const projectRegion = useProjectRegion();

  // Project state for file uploads
  const [projectDetails, setProjectDetails] = createSignal<any>(null);
  const [currentFolder, setCurrentFolder] = createSignal<any>(null);

  // Create issue mutation - only if API services are ready
  const createIssueMutation = apiServices.issueApi.createIssueMutation();

  // Update issue mutation - only if API services are ready
  const updateIssueMutation = apiServices.issueApi.updateIssueMutation();

  // Delete issue mutation - only if API services are ready
  const deleteIssueMutation = apiServices.issueApi.deleteIssueMutation();

  // Create tag mutation - only if API services are ready
  const createTagMutation = apiServices.tagApi.createTagMutation();

  // Add objects to tag mutation - only if API services are ready
  const addObjectsToTagMutation = apiServices.tagApi.addObjectsToTagMutation();

  // Follow resource mutation - only if API services are ready
  const followResourceMutation = apiServices.followApi.followResourceMutation();

  // Unfollow resource mutation - only if API services are ready
  const unfollowResourceMutation =
    apiServices.followApi.unfollowResourceMutation();

  // Update followers mutation - only if API services are ready
  const updateFollowersMutation =
    apiServices.followApi.updateFollowersMutation();

  // Delete view mutation - only if API services are ready
  const deleteViewMutation = apiServices.viewApi.deleteViewMutation();

  // Create view mutation - only if API services are ready
  const createViewMutation = apiServices.viewApi.createViewMutation();

  // Object link mutations - only if API services are ready
  const createObjectLinkMutation =
    apiServices.objectLinkApi.createObjectLinkMutation();
  const deleteObjectLinkMutation =
    apiServices.objectLinkApi.deleteObjectLinkMutation();

  // Reference mutations - only if API services are ready
  const addReferenceMutation = referenceApiService.addReferenceMutation();

  // Query to get followers for the selected issue
  const resourceFollowersQuery =
    apiServices.followApi.getResourceFollowersQuery(
      // eslint-disable-next-line solid/reactivity -- This function is used as a query parameter and doesn't need to be reactive
      () => props.contextId,
      // eslint-disable-next-line solid/reactivity -- This function is used as a query parameter and doesn't need to be reactive
      () =>
        props.selectedIssue ? `issues:${props.selectedIssue.id}` : undefined
    );

  // Check if current user is already following this issue
  const isCurrentUserFollowing = createMemo(() => {
    if (!resourceFollowersQuery.data?.items || !props.currentUser?.tiduuid) {
      return false;
    }

    const currentUserFollowerId = `users:${props.currentUser.tiduuid}`;
    const isFollowing = resourceFollowersQuery.data.items.some(
      (follow) => follow.followerId === currentUserFollowerId
    );
    return isFollowing;
  });

  // Query to get tags for the selected issue - only when selectedIssue exists
  const issueTagsQuery = createMemo(() => {
    // Access reactive props within tracked scope
    const selectedIssue = props.selectedIssue;
    const contextId = props.contextId;
    if (!selectedIssue?.id) {
      return null;
    }
    const query = apiServices.tagApi.getTagsQuery(() => ({
      query: {
        objectId: selectedIssue.id,
        objectType: 'ISSUE',
        miniRep: true,
        contextId: contextId,
      },
    }));
    return query;
  });

  // Shared TanStack query client (used for invalidating object-link queries
  // when the host reports link/unlink activity)
  const queryClient = useQueryClient();

  // Query to list object links targeting the selected issue. Used to compute
  // the "Linked model objects" count and keep it in sync with the backend.
  // Mirrors the legacy `tcweb` request shape: only `projectId`, `type`, `id`,
  // and `contextId` (required for ISSUE target) are sent.
  const issueLinksQuery =
    apiServices.objectLinkApi.listLinksForTargetEntityQuery(
      // eslint-disable-next-line solid/reactivity -- reactive accessor for tanstack query
      () => {
        const selectedIssue = props.selectedIssue;
        const projectId = props.projectId;
        const contextId = props.contextId;
        if (!selectedIssue?.id || !projectId || !contextId) {
          return undefined;
        }
        return {
          query: {
            projectId,
            type: 'ISSUE',
            id: selectedIssue.id,
            contextId,
          },
        };
      }
    );

  // Total count of linked model objects across all target links for the
  // currently selected issue. Mirrors the legacy TopicsRightPanel formula:
  // sum of `link.source.data.length` across every link returned.
  const linkedObjectsCount = createMemo<number>(() => {
    const links = issueLinksQuery.data;
    if (!links || links.length === 0) {
      return 0;
    }
    return links.reduce(
      (sum, link) => sum + (link.source?.data?.length ?? 0),
      0
    );
  });

  // Resolved type-icon display for the selected issue's "type" chip. Memoised
  // so we only run the image/font-icon dispatch once per dependency change
  // instead of twice inside the JSX (once for `when`, once for `fallback`).
  const selectedIssueTypeIcon = createMemo(() =>
    resolveTypeIconDisplay(
      props.configurationData ?? null,
      props.selectedIssue?.type,
      props.iconsList
    )
  );
  const selectedIssueTypeIconImageSrc = (): string | undefined => {
    const r = selectedIssueTypeIcon();
    return r.kind === 'image' ? r.src : undefined;
  };
  const selectedIssueTypeIconFontClass = (): string => {
    const r = selectedIssueTypeIcon();
    return r.kind === 'font' ? r.className : 'tc-icon-bcf';
  };

  // Helper function to get TCPS API base URL for comments
  const getTcpsApiBaseUrl = (): string => {
    const regionData = projectRegion;
    const projectLocation = regionData.project?.location;

    // Get regional TCPS API domain
    const tcpsApiDomain =
      configuration.getRegionalTcpsApiDomain(projectLocation);
    return tcpsApiDomain;
  };

  // Helper function to create comment for an issue
  const createCommentForIssue = async (
    objectId: string,
    commentData: ICommentData,
    projectId: string,
    contextId: string | undefined
  ): Promise<void> => {
    if (!contextId) {
      throw new Error('Context ID is required for comment creation');
    }

    if (!objectId) {
      throw new Error('Object ID is required for comment creation');
    }

    // We need to create the comment within a CommentProvider context
    // This will be handled by wrapping the form submission in CommentProvider
    const commentPayload = {
      comment: commentData.comment,
      objectId: objectId,
      projectId: projectId,
      objectType: 'ISSUE' as const,
      contextId: contextId,
    };

    // Create a properly configured client for comment API
    const tcpsApiDomain = getTcpsApiBaseUrl();
    const client = createClient({ baseUrl: tcpsApiDomain, global: false });

    // Configure client with proper headers
    // Capture accessToken in tracked scope before setting up interceptor
    const accessToken = props.accessToken;
    client.interceptors.request.use((request) => {
      request.headers.set('Authorization', `Bearer ${accessToken}`);
      request.headers.set('Content-Type', 'application/json');
      return request;
    });

    // Create a minimal QueryClient for the API service
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          refetchOnMount: false,
          refetchOnReconnect: false,
          retry: false,
          staleTime: Infinity,
        },
      },
    });

    // Import the comment API service directly
    const { CommentApiService } = await import('@tc-comments/providers');
    const commentApiService = new CommentApiService(client, queryClient);

    // Create the comment
    const commentResult = await commentApiService.createComment(commentPayload);

    // Save file attachments if present
    if (commentData.attachments && commentData.attachments.length > 0) {
      const fileAttachments = commentData.attachments.filter(
        (att) => att.type === 'FILE'
      );

      if (fileAttachments.length > 0) {
        const attachmentRequests = fileAttachments.map((attachment) => ({
          type: attachment.type,
          id: attachment.id,
        }));

        const attachmentResponse = await postAddattachments({
          client: client,
          path: { commentId: commentResult.comment.id },
          body: attachmentRequests,
        });

        if (attachmentResponse.error) {
          throw new Error('Failed to add comment attachments');
        }
      }
    }

    // Save the captured view and attach it to the comment
    if (commentData.createViewRequest) {
      const createdView = await createViewMutation.mutateAsync({
        body: commentData.createViewRequest,
      });

      if (props.reservedViewDataConfig) {
        try {
          await apiServices.viewApi.saveReservedViewData(
            createdView.id,
            props.reservedViewDataConfig.appName,
            props.reservedViewDataConfig.getReservedData({
              viewId: createdView.id,
              issueId: objectId,
            })
          );
        } catch (reservedError: unknown) {
          console.error(
            'Failed to save reserved view data for comment view',
            { viewId: createdView.id },
            reservedError
          );
        }
      }

      const viewAttachmentResponse = await postAddattachments({
        client: client,
        path: { commentId: commentResult.comment.id },
        body: [{ type: 'VIEW', id: createdView.id }],
      });

      if (viewAttachmentResponse.error) {
        throw new Error('Failed to attach view to comment');
      }
    }
  };

  // Fetch project details when needed
  const fetchProjectDetails = async (): Promise<ProjectDetailsResponse> => {
    const cached = projectDetails();
    if (cached) return cached;

    try {
      const response = await getProjectDetails({
        client: (fileApiService as any).client, // Access the underlying client
        path: { projectId: props.projectId },
      });

      if (response.error) {
        throw new Error('Failed to fetch project details');
      }

      const project = response.data;
      if (!project) {
        throw new Error('Failed to fetch project details: no data returned');
      }
      setProjectDetails(project);
      return project;
    } catch (error) {
      console.error('Error fetching project details:', error);
      throw error;
    }
  };

  // Fetch or create default folder for issue attachments
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- CDM.TCPSClient expects a specific structure that matches TCPS API response
  const getDefaultAttachmentFolder = async (
    project: ProjectDetailsResponse
  ): Promise<any> => {
    const cached = currentFolder();
    if (cached) return cached;

    try {
      const ISSUES_DEFAULT_FOLDER_NAME = 'Topics References';

      // List root folder contents to check if Issues folder exists
      const listResponse = await listItemsinaFolder({
        client: (fileApiService as any).client,
        path: { folderId: project.rootId },
      });

      if (listResponse.error) {
        throw new Error('Failed to list folder contents');
      }

      // Check if Issues folder already exists
      const existingFolder = listResponse.data?.find(
        (item: any) =>
          item.type === 'FOLDER' && item.name === ISSUES_DEFAULT_FOLDER_NAME
      );

      if (existingFolder) {
        setCurrentFolder(existingFolder);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TCPS API response type matches CDM expected structure
        return existingFolder as any;
      }

      // Create Issues folder if it doesn't exist
      const createFolderData: CreateaNewFolderData = {
        body: {
          name: ISSUES_DEFAULT_FOLDER_NAME,
          parentId: project.rootId,
        },
      };

      const createResponse = await createaNewFolder({
        client: (fileApiService as any).client,
        ...createFolderData,
      });

      if (createResponse.error) {
        throw new Error('Failed to create Issues folder');
      }

      const newFolder = createResponse.data;
      if (!newFolder) {
        throw new Error('Failed to create folder: no data returned');
      }
      setCurrentFolder(newFolder);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TCPS API response type matches CDM expected structure
      return newFolder as any;
    } catch (error) {
      console.error('Error managing default attachment folder:', error);
      // Fallback to root folder if Issues folder creation fails
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- CDM.TCPSClient accepts minimal folder structure
      const rootFolder: any = { id: project.rootId, name: 'Root' };
      setCurrentFolder(rootFolder);
      return rootFolder;
    }
  };

  // Helper functions to get options from configuration data
  const getTypeOptions = (): string[] => {
    if (!props.configurationData?.type) {
      return [
        translate('TC.Common.Type.Undefined'),
        translate('TC.Common.Type.Comment'),
        translate('TC.Common.Type.Issue'),
        translate('TC.Common.Type.Request'),
        translate('TC.Common.Type.Fault'),
        translate('TC.Common.Type.Inquiry'),
        translate('TC.Common.Type.Solution'),
        translate('TC.Common.Type.Remark'),
        translate('TC.Common.Type.Clash'),
      ];
    }
    return props.configurationData.type
      .filter((item: any) => item.visible !== false)
      .map((item: any) => item.name || 'Unnamed');
  };

  const getPriorityOptions = (): string[] => {
    if (!props.configurationData?.priority) {
      return [
        translate('TC.Common.Priority.Low'),
        translate('TC.Common.Priority.Normal'),
        translate('TC.Common.Priority.High'),
        translate('TC.Common.Priority.Critical'),
      ];
    }
    return props.configurationData.priority
      .filter((item: any) => item.visible !== false)
      .map((item: any) => item.name || 'Unnamed');
  };

  const getStatusOptions = (): string[] => {
    if (!props.configurationData?.status) {
      return [
        translate('NEW'),
        translate('TC.Common.Status.InProgress'),
        translate('TC.Common.Status.Waiting'),
        translate('TC.Common.Status.Done'),
        translate('TC.Common.Status.Closed'),
      ];
    }
    return props.configurationData.status
      .filter((item: any) => item.visible !== false)
      .map((item: any) => item.name || 'Unnamed');
  };

  // Initialize web components and add document event listeners
  onMount(() => {
    initWebComponents();
    defineSolidConfirmPopup();
    defineSolidSpinner();
    const handleDocumentSelectionChanged = (event: CustomEvent): void => {
      const selectedOption = event.detail.selected;
      if (selectedOption === translate('exportLabel')) {
        setShowExportDialog(true);
      }
    };

    // Handle 3D events from host
    const handle3DEventFromHost = (event: CustomEvent): void => {
      const { type, data } = event.detail as { type: I3DActionType; data: any };

      // Update UI based on host events
      switch (type) {
        case 'add-issue-marker':
          // Host added a marker, update UI accordingly
          if (data && data.markerId) {
            const newMarker: IMarker = {
              id: data.markerId,
              position: data.position || { x: 0, y: 0, z: 0 },
              issueId: props.selectedIssue?.id || '',
              contextId: props.contextId,
              createdAt: new Date(),
            };
            setCurrentMarker(newMarker);
          }
          break;
        case 'remove-issue-marker':
          // Host removed a marker, clear marker state
          setCurrentMarker(null);
          break;
        case 'view-issue-model-objects':
          // Host updated model objects view
          break;
        case 'link-issue-model-objects':
        case 'unlink-issue-model-objects':
        case 'unlink-all-issue-model-objects':
          // Host reported a change to linked objects; refetch the target-entity
          // link list (used by `issueLinksQuery` / `linkedObjectsCount`) so the
          // "Linked model objects" count stays in sync with the server.
          // Narrow to `['object-links', 'target']` so we don't also invalidate
          // unrelated `listLinkedEntitiesForObjects` queries.
          void queryClient.invalidateQueries({
            queryKey: ['object-links', 'target'],
          });
          break;
      }

      // Clear any previous action result to show fresh feedback
      setThreeDActionResult(null);
    };

    // Add event listeners to document
    document.addEventListener(
      'selection-changed',
      handleDocumentSelectionChanged as EventListener
    );

    document.addEventListener(
      '3d-host-update',
      handle3DEventFromHost as EventListener
    );

    // Cleanup on unmount
    return () => {
      document.removeEventListener(
        'selection-changed',
        handleDocumentSelectionChanged as EventListener
      );
      document.removeEventListener(
        '3d-host-update',
        handle3DEventFromHost as EventListener
      );
    };
  });

  // Tag state management
  const [assigneeTags, setAssigneeTags] = createSignal<ITagItem[]>([]);
  const [followersTags, setFollowersTags] = createSignal<ITagItem[]>([]);
  const [issueTags, setIssueTags] = createSignal<ITagItem[]>([]);

  // Helper function to reset form and tag state
  const resetFormAndTags = (): void => {
    issueForm.reset();
    setAssigneeTags([]);
    setFollowersTags([]);
    setIssueTags([]);
    setFormReferences([]);
    setShowAddComment(false);
    setCommentData(undefined);
    setIssueViewPreviewData(null);
  };

  // Helper function to populate form with issue data
  const populateFormWithIssueData = (issue: any): void => {
    // Set form values
    issueForm.setFieldValue('title', issue.title || '');
    issueForm.setFieldValue('description', issue.description || '');
    issueForm.setFieldValue('type', issue.type || '');
    issueForm.setFieldValue('priority', issue.priority || '');
    issueForm.setFieldValue('status', issue.status || '');
    // Convert due date from MM/DD/YYYY (API format) to YYYY-MM-DD (date picker format)
    issueForm.setFieldValue(
      'dueDate',
      convertMMDDYYYYtoISO(issue.dueDate) || ''
    );

    // Convert assignees back to tag format
    const assigneeTags = (issue.assignees || []).map((assignee: string) => {
      const tiduuid = assignee.startsWith('users:')
        ? assignee.substring(6) // eslint-disable-line no-magic-numbers -- 6 is the length of 'users:' prefix
        : assignee;
      const userName = getUserNameByTiduuid(tiduuid, props.projectUsersData);
      return {
        label: userName,
        value: `${tiduuid}||USER||${tiduuid}`,
        isValid: true,
      };
    });
    setAssigneeTags(assigneeTags);

    // Convert followers from query data to tag format
    const followersTags = (resourceFollowersQuery.data?.items || [])
      .map((follow) => {
        const USERS_PREFIX = 'users:';
        const USERS_PREFIX_LENGTH = USERS_PREFIX.length;
        const tiduuid = follow.followerId?.startsWith(USERS_PREFIX)
          ? follow.followerId.substring(USERS_PREFIX_LENGTH)
          : follow.followerId;

        if (!tiduuid) return null;

        const userName = getUserNameByTiduuid(tiduuid, props.projectUsersData);
        return {
          label: userName,
          value: `${tiduuid}||USER||${tiduuid}`,
          isValid: true,
        };
      })
      .filter((tag): tag is ITagItem => tag !== null);
    setFollowersTags(followersTags);

    // Convert tags back to tag format using the query data
    const queryTags = issueTagsQuery()?.data || [];

    const issueTags = queryTags.map((tag: any) => ({
      label: tag.label || tag.name,
      value: tag.id || tag.label || tag.name,
      isValid: true,
    }));
    setIssueTags(issueTags);
  };

  // Comment-related state and handlers
  const [addedComment, setAddedComment] = createSignal<IComment | undefined>(
    undefined
  );

  const handleCommentsCount = (count: number): void => {
    setCommentsCount(count);
  };

  const handleCommentDetail = (_commentDetail: IComment): void => {
    // Transform IComment to ITopicComment format for compatibility
    // Handle comment detail logic here
  };

  const handleCommentAdded = (comment: IComment): void => {
    // Set the added comment to trigger update in CommentContainer
    setAddedComment(comment);
  };

  const removeComment = (): void => {
    setCommentData(undefined);
  };

  const handleCommentDataChange = (data: ICommentData): void => {
    // Store comment data in real-time as user types/changes attachments
    setCommentData(data);
  };

  const isCommentActionAllowed = (action: string): boolean => {
    // Implement comment action permission logic
    return props.isCommentActionAllowed
      ? props.isCommentActionAllowed(action)
      : true;
  };

  // Initialize currentMarker from the selected issue's saved marker data
  createEffect(() => {
    const issue = props.selectedIssue;
    if (issue?.marker?.point) {
      setCurrentMarker({
        id: `marker_${issue.id}`,
        position: {
          x: issue.marker.point.x ?? 0,
          y: issue.marker.point.y ?? 0,
          z: issue.marker.point.z ?? 0,
        },
        issueId: issue.id,
        contextId: props.contextId,
        createdAt: issue.createdAt ? new Date(issue.createdAt) : new Date(),
      });
    } else {
      setCurrentMarker(null);
    }
  });

  // Reset tab data when navigating between issues (previous/next)
  // This provides immediate visual feedback while new data loads
  createEffect(
    on(
      () => props.selectedIssue?.id,
      (currentId, previousId) => {
        if (currentId && previousId && currentId !== previousId) {
          // Reset counts to show loading state while new data loads
          setViewpointCount(0);
          setCommentsCount(0);
          setReferenceCount(0);
          // Reset comment-related state
          setAddedComment(undefined);
          setShowAddComment(false);
          setCommentData(undefined);
          // Reset preview state
          setIsPreviewOn(false);
          setThumbnailBlobUrl('');
          setCurrentMarker(null);
        }
      }
    )
  );

  createEffect(
    on(
      () => props.selectedIssue?.id,
      (issueId) => {
        if (issueId && configuration.isIssueAclEnabled() && props.contextId) {
          void loadSharedWithChips(issueId);
        } else {
          setSharedWithChips([]);
          setIsSharedWithLoaded(false);
        }
      }
    )
  );

  // Hydrate marker state from the selected issue's persisted data
  createEffect(
    on(
      () => props.selectedIssue,
      (issue) => {
        if (issue?.marker?.point) {
          setCurrentMarker({
            id: `marker_${issue.id}`,
            position: {
              x: issue.marker.point.x ?? 0,
              y: issue.marker.point.y ?? 0,
              z: issue.marker.point.z ?? 0,
            },
            issueId: issue.id,
            contextId: props.contextId,
            createdAt: issue.createdAt ? new Date(issue.createdAt) : new Date(),
          });
        } else {
          setCurrentMarker(null);
        }
      }
    )
  );

  // Tag event handlers - can handle both direct callback and CustomEvent
  const handleAssigneeTagChange = (
    tagsOrEvent: ITagItem[] | CustomEvent
  ): void => {
    let tags: ITagItem[];

    if (Array.isArray(tagsOrEvent)) {
      // Direct callback from prop
      tags = tagsOrEvent;
    } else {
      // CustomEvent from web component
      tags = (tagsOrEvent as CustomEvent).detail.tags as ITagItem[];
    }

    setAssigneeTags([...tags]);
  };

  const handleIssueTagChange = (
    tagsOrEvent: ITagItem[] | CustomEvent
  ): void => {
    let tags: ITagItem[];

    if (Array.isArray(tagsOrEvent)) {
      // Direct callback from prop
      tags = tagsOrEvent;
    } else {
      // CustomEvent from web component
      tags = (tagsOrEvent as CustomEvent).detail.tags as ITagItem[];
    }

    setIssueTags([...tags]);
  };

  const handleFollowersTagChange = (
    tagsOrEvent: ITagItem[] | CustomEvent
  ): void => {
    let tags: ITagItem[];

    if (Array.isArray(tagsOrEvent)) {
      // Direct callback from prop
      tags = tagsOrEvent;
    } else {
      // CustomEvent from web component
      tags = (tagsOrEvent as CustomEvent).detail.tags as ITagItem[];
    }

    setFollowersTags([...tags]);
  };

  // Form reference handlers
  const handleFormAddReference = (): void => {
    setShowFormAttachmentPicker(true);
  };

  const handleFormAttachmentPickerSubmit = async (
    filesList: any
  ): Promise<void> => {
    try {
      // Process selected files and convert to IReference format
      if (filesList.type === 'FILE_FROM_CONNECT') {
        // Handle files from Trimble Connect
        const selectedFiles = Array.isArray(filesList.selectedObject)
          ? filesList.selectedObject
          : [filesList.selectedObject];

        for (const selectedFile of selectedFiles) {
          const reference: IReference = {
            id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID for form state
            description:
              selectedFile.name || selectedFile.displayName || 'Unknown File',
            type: 'FILE',
            resourceId:
              selectedFile.id || selectedFile.versionId || selectedFile.fileId,
            name: selectedFile.name || selectedFile.displayName,
            versionId: selectedFile.versionId,
          };

          setFormReferences((prev) => [...prev, reference]);
        }
      } else if (filesList.type === 'FILE_FROM_COMPUTER') {
        // Handle files from computer upload - store file data for later upload
        try {
          const project = await fetchProjectDetails();
          const defaultFolder = await getDefaultAttachmentFolder(project);

          const selectedFiles = Array.isArray(filesList.selectedObject)
            ? filesList.selectedObject
            : [filesList.selectedObject];

          // Configure TCPS URL for CDM.TCPSClient
          const regionData = projectRegion;
          const projectLocation = regionData.project?.location;
          const tcpsApiDomain =
            configuration.getRegionalTcpsApiDomain(projectLocation);

          // Parse the TCPS API URL to extract just the domain without protocol
          let baseOrigin;
          try {
            const url = new URL(tcpsApiDomain);
            baseOrigin = url.host;
          } catch (error) {
            console.error('Failed to parse TCPS URL:', tcpsApiDomain, error);
            let cleanUrl = tcpsApiDomain;
            if (cleanUrl.includes('/tc/api/')) {
              cleanUrl = cleanUrl.split('/tc/api/')[0];
            }
            baseOrigin = cleanUrl.replace(/^https?:\/\//, '');
          }

          // Create project payload for CDM.TCPSClient with base origin
          const projectPayload = {
            id: project.id,
            name: project.name,
            rootId: project.rootId,
            origin: baseOrigin,
          };

          // Use CDM.TCPSClient.uploadFileContent for file upload
          CDM.TCPSClient.uploadFileContent(
            projectPayload,
            selectedFiles,
            defaultFolder.id,
            'FOLDER'
          ).then(
            (fileEntry: ServiceResponse<FileEntry>[]) => {
              // Create references for each uploaded file
              fileEntry.forEach((fileObject: ServiceResponse<FileEntry>) => {
                const reference: IReference = {
                  id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID for form state
                  description: fileObject.data.name,
                  type: 'FILE',
                  resourceId: fileObject.data.id,
                  name: fileObject.data.name,
                  versionId: fileObject.data.versionId,
                };

                setFormReferences((prev) => [...prev, reference]);
              });
            },
            (error) => {
              console.error('Failed to upload files:', error);
              throw error;
            }
          );
        } catch (error) {
          console.error('Failed to process FILE_FROM_COMPUTER:', error);
          throw error;
        }
      } else if (filesList.type === 'LINK') {
        const reference: IReference = {
          id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID for form state
          description:
            filesList.selectedObject.displayText ||
            filesList.selectedObject.url,
          type: 'URL',
          url: filesList.selectedObject.url,
        };

        setFormReferences((prev) => [...prev, reference]);
      }

      setShowFormAttachmentPicker(false);
    } catch (error) {
      console.error('Failed to add form references:', error);
    }
  };

  const handleFormAttachmentPickerClose = (): void => {
    setShowFormAttachmentPicker(false);
  };

  const handleFormReferenceRemove = (referenceId: string): void => {
    setFormReferences((prev) => prev.filter((ref) => ref.id !== referenceId));
  };

  // No need for manual event listeners anymore since we're using prop callbacks

  // Helper function to reset panel-specific state
  const resetPanelState = (): void => {
    // Reset tab port to default
    setTabPort('VIEW');
    setCommentsCount(0);
    setReferenceCount(0);
    // Reset comment-related state
    setAddedComment(undefined);
    // Reset any other panel-specific state that should be cleared
    // Note: singleReferencesAttachments creates local state that will be cleared when component unmounts
  };

  const handleClose = (): void => {
    // Notify host to cancel any pending pick and remove markers
    props.threeDCallbacks
      ?.onRemoveIssueMarker?.({
        type: 'remove-issue-marker',
        issueId: props.selectedIssue?.id ?? '',
        contextId: props.contextId,
        payload: { initiator: 'panel-close' },
      })
      ?.catch((error: unknown) => {
        console.warn('Failed to remove issue marker on panel close', error);
      });

    // Reset form state
    resetFormAndTags();
    // Reset panel state
    resetPanelState();
    props.onClose();
  };

  // Navigation helper functions
  const getCurrentIssueIndex = (): number => {
    if (!props.selectedIssue || !props.issuesList) {
      return -1;
    }

    // Find issue by ID (both should now be consistently transformed)
    return props.issuesList.findIndex(
      (issue) => issue.id === props.selectedIssue!.id
    );
  };

  const canNavigatePrevious = (): boolean => {
    const currentIndex = getCurrentIssueIndex();
    return currentIndex > 0;
  };

  const canNavigateNext = (): boolean => {
    const currentIndex = getCurrentIssueIndex();
    return currentIndex >= 0 && currentIndex < props.issuesList.length - 1;
  };

  // Navigation handlers
  const handleNavigatePrevious = (): void => {
    if (!canNavigatePrevious()) return;

    const currentIndex = getCurrentIssueIndex();
    const previousIssue = props.issuesList[currentIndex - 1];
    if (previousIssue) {
      props.onSelectIssue(previousIssue);
    }
  };

  const handleNavigateNext = (): void => {
    if (!canNavigateNext()) return;

    const currentIndex = getCurrentIssueIndex();
    const nextIssue = props.issuesList[currentIndex + 1];
    if (nextIssue) {
      props.onSelectIssue(nextIssue);
    }
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TS6133: Reserved for future use
  const _handleView3D = (): void => {
    // Handle 3D view logic here
  };

  const grantReadAccessOnFollow = async (issueId: string): Promise<void> => {
    if (!props.currentUser?.tiduuid || !props.contextId) return;

    const HTTP_NOT_FOUND = 404;

    try {
      await grantReadAccessOnFollowForIssue({
        client: apiServices.issueManagementClient,
        contextId: props.contextId,
        issueId,
        currentUserTiduuid: props.currentUser.tiduuid,
      });
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- checking response status from legacy error shape
      if ((error as any)?.response?.status === HTTP_NOT_FOUND) return;
      throw error;
    }
  };

  let sharedWithChipsRequestId = 0;

  const loadSharedWithChips = async (issueId: string): Promise<void> => {
    if (!props.contextId) {
      setIsSharedWithLoaded(true);
      return;
    }
    const requestId = ++sharedWithChipsRequestId;
    setIsSharedWithLoaded(false);
    try {
      const response = await getIssuePermissions({
        client: apiServices.issueManagementClient,
        path: { contextId: props.contextId, issueId },
        query: { fields: ['inheritedAcl'] },
      });
      if (requestId !== sharedWithChipsRequestId) return;
      if (!response.data) {
        setSharedWithChips([]);
        setIsSharedWithLoaded(true);
        return;
      }
      const acl = parseExplicitIssueAcl(response.data.acl);
      const inheritedAcl = response.data.inheritedAcl as
        | IssueAclMap
        | undefined;
      const chips = buildSharedWithChips(
        acl,
        inheritedAcl,
        props.projectUsersData ?? [],
        props.selectedIssue?.createdBy
      );
      setSharedWithChips(chips);
      setIsSharedWithLoaded(true);
    } catch {
      if (requestId !== sharedWithChipsRequestId) return;
      setSharedWithChips([]);
      setIsSharedWithLoaded(true);
    }
  };

  // Follow functionality
  const handleFollow = async (): Promise<void> => {
    if (!props.selectedIssue?.id || !props.contextId) {
      console.warn('Cannot follow issue: missing issue ID or context ID');
      return;
    }

    try {
      await followResourceMutation.mutateAsync({
        path: {
          contextId: props.contextId,
        },
        body: {
          resourceId: `issues:${props.selectedIssue.id}`,
        },
      });

      if (configuration.isIssueAclEnabled()) {
        try {
          await grantReadAccessOnFollow(props.selectedIssue.id);
        } catch {
          console.error('ACL grant failed after follow');
          showSnackbar({
            text:
              translate('TC.Topic.FollowAclWarning') ||
              'Followed successfully but read access could not be granted automatically',
            type: 'warning',
          });
        }
      }
    } catch {
      console.error('Failed to follow issue');
    }
  };

  // Unfollow functionality
  const handleUnfollow = async (): Promise<void> => {
    if (!props.selectedIssue?.id || !props.contextId) {
      console.warn('Cannot unfollow issue: missing issue ID or context ID');
      return;
    }

    try {
      // Unfollow the issue resource
      await unfollowResourceMutation.mutateAsync({
        path: {
          contextId: props.contextId,
        },
        query: {
          resourceId: `issues:${props.selectedIssue.id}`,
        },
      });

      // The unfollowResourceMutation automatically invalidates followers queries on success
      // TODO: Show success feedback to user
    } catch (error) {
      console.error('Failed to unfollow issue:', error);
      // TODO: Show error feedback to user
    }
  };

  // 3D Action handlers
  const handle3DAction = async (
    actionType: I3DActionType,
    payload?: any
  ): Promise<void> => {
    if (!props.selectedIssue?.id) {
      console.warn('No selected issue for 3D action');
      return;
    }

    const callbackKey = `on${actionType
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('')}`;

    const callback = props.threeDCallbacks?.[
      callbackKey as keyof typeof props.threeDCallbacks
    ] as
      | ((request: I3DActionRequest) => Promise<I3DActionResponse>)
      | undefined;

    if (!callback) {
      console.warn(`No callback provided for 3D action: ${actionType}`);
      return;
    }

    const request: I3DActionRequest = {
      type: actionType,
      issueId: props.selectedIssue.id,
      contextId: props.contextId,
      payload,
    };

    try {
      setThreeDActionLoading(actionType);
      setThreeDActionResult(null);

      const response = await callback(request);
      setThreeDActionResult(response);

      // Dispatch custom event for additional host communication
      const event = new CustomEvent('3d-action-completed', {
        detail: { request, response },
      });
      document.dispatchEvent(event);
    } catch (error) {
      console.error(`3D action ${actionType} failed:`, error);
      setThreeDActionResult({
        type: actionType,
        success: false,
        message: `Failed to execute ${actionType}`,
      });
    } finally {
      setThreeDActionLoading(null);
    }
  };

  // Handle add issue marker from host callback + API update
  const handleAddIssueMarker = async (): Promise<void> => {
    if (!props.selectedIssue?.id || !props.contextId) {
      console.warn('Cannot add issue marker: missing required data');
      return;
    }

    const callbackKey = 'onAddIssueMarker';
    const callback = props.threeDCallbacks?.[callbackKey];

    if (!callback) {
      console.warn('No callback provided for add issue marker');
      showSnackbar({
        text: 'No 3D viewer available. Please open a 3D viewer to add a marker.',
        type: 'error',
        timeoutMilliseconds: 4000,
      });
      return;
    }

    const request: I3DActionRequest = {
      type: 'add-issue-marker',
      issueId: props.selectedIssue.id,
      contextId: props.contextId,
      payload: {
        issueType: props.selectedIssue.type,
        priority: props.selectedIssue.priority,
        status: props.selectedIssue.status,
      },
    };

    let markerPlacedByHost = false;

    try {
      setThreeDActionLoading('add-issue-marker');
      setThreeDActionResult(null);

      // Get marker data from host
      const hostResponse = await callback(request);

      if (!hostResponse.success || !hostResponse.data) {
        throw new Error(
          hostResponse.message || 'Failed to capture marker from host'
        );
      }

      // Marker is now visible in the 3D viewer — track this so the catch can roll it back
      markerPlacedByHost = true;

      // Update issue with marker data using host response merged with current issue data
      const updateData = {
        url: '/context/{contextId}/issues/{issueId}' as const,
        path: {
          contextId: props.contextId,
          issueId: props.selectedIssue.id,
        },
        body: {
          // Current issue data from main list
          title: props.selectedIssue.title,
          description: props.selectedIssue.description,
          type: props.selectedIssue.type,
          status: props.selectedIssue.status,
          priority: props.selectedIssue.priority,
          dueDate: props.selectedIssue.dueDate,
          assignees: props.selectedIssue.assignees,
          // Merge with host response data (marker update)
          ...hostResponse.data,
        },
      };

      // Update the issue using API
      await updateIssueMutation.mutateAsync(updateData);

      // Show success feedback
      showSnackbar({
        text: 'Marker added successfully',
        type: 'success',
        timeoutMilliseconds: 3000,
      });

      setThreeDActionResult({
        type: 'add-issue-marker',
        success: true,
        message: 'Marker added successfully',
        data: { markerId: hostResponse.data.marker?.point },
      });

      // Update local marker state
      if (hostResponse.data.marker?.point) {
        const newMarker: IMarker = {
          id: `marker_${Date.now()}`,
          position: hostResponse.data.marker.point,
          issueId: props.selectedIssue.id,
          contextId: props.contextId,
          createdAt: new Date(),
        };
        setCurrentMarker(newMarker);
      }

      // Dispatch custom event for host communication
      const event = new CustomEvent('3d-action-completed', {
        detail: {
          request,
          response: {
            type: 'add-issue-marker',
            success: true,
            data: { markerId: hostResponse.data.marker?.point },
          },
        },
      });
      document.dispatchEvent(event);
    } catch (error) {
      // If the host already placed the marker in the viewer but the API update failed,
      // roll back the 3D icon so the viewer stays consistent with the persisted state.
      if (markerPlacedByHost) {
        void props.threeDCallbacks?.onRemoveIssueMarker?.({
          type: 'remove-issue-marker',
          issueId: props.selectedIssue.id,
          contextId: props.contextId,
        });
      }

      console.error('Add issue marker failed:', error);

      // Show error feedback
      showSnackbar({
        text: 'Failed to add marker. Please try again.',
        type: 'error',
        timeoutMilliseconds: 4000,
      });

      setThreeDActionResult({
        type: 'add-issue-marker',
        success: false,
        message: `Failed to add marker: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setThreeDActionLoading(null);
    }
  };

  // Handle remove issue marker from host callback + API update
  const handleRemoveIssueMarker = async (): Promise<void> => {
    if (!props.selectedIssue?.id || !props.contextId) {
      console.warn('Cannot remove issue marker: missing required data');
      return;
    }

    const callbackKey = 'onRemoveIssueMarker';
    const callback = props.threeDCallbacks?.[callbackKey];

    if (!callback) {
      console.warn('No callback provided for remove issue marker');
      return;
    }

    const request: I3DActionRequest = {
      type: 'remove-issue-marker',
      issueId: props.selectedIssue.id,
      contextId: props.contextId,
    };

    try {
      setThreeDActionLoading('remove-issue-marker');
      setThreeDActionResult(null);

      // Get confirmation from host
      const hostResponse = await callback(request);

      if (!hostResponse.success || !hostResponse.data) {
        throw new Error(
          hostResponse.message || 'Failed to remove marker from host'
        );
      }

      // Update issue to remove marker using host response merged with current issue data
      const updateData = {
        url: '/context/{contextId}/issues/{issueId}' as const,
        path: {
          contextId: props.contextId,
          issueId: props.selectedIssue.id,
        },
        body: {
          // Current issue data from main list
          title: props.selectedIssue.title,
          description: props.selectedIssue.description,
          type: props.selectedIssue.type,
          status: props.selectedIssue.status,
          priority: props.selectedIssue.priority,
          dueDate: props.selectedIssue.dueDate,
          assignees: props.selectedIssue.assignees,
          // Merge with host response data (marker removal)
          ...hostResponse.data,
        },
      };

      // Update the issue using API
      await updateIssueMutation.mutateAsync(updateData);

      // Show success feedback
      showSnackbar({
        text: 'Marker removed successfully',
        type: 'success',
        timeoutMilliseconds: 3000,
      });

      setThreeDActionResult({
        type: 'remove-issue-marker',
        success: true,
        message: 'Marker removed successfully',
      });

      // Clear local marker state
      setCurrentMarker(null);

      // Dispatch custom event for host communication
      const event = new CustomEvent('3d-action-completed', {
        detail: {
          request,
          response: {
            type: 'remove-issue-marker',
            success: true,
            data: { removed: true },
          },
        },
      });
      document.dispatchEvent(event);
    } catch (error) {
      console.error('Remove issue marker failed:', error);

      // Show error feedback
      showSnackbar({
        text: 'Failed to remove marker. Please try again.',
        type: 'error',
        timeoutMilliseconds: 4000,
      });

      setThreeDActionResult({
        type: 'remove-issue-marker',
        success: false,
        message: `Failed to remove marker: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setThreeDActionLoading(null);
    }
  };
  const handleViewIssueModelObjects = (): Promise<void> =>
    handle3DAction('view-issue-model-objects');

  // Handle link issue model objects from host callback + API create
  const handleLinkIssueModelObjects = async (): Promise<void> => {
    if (!props.selectedIssue?.id || !props.contextId) {
      console.warn('Cannot link issue model objects: missing required data');
      return;
    }

    const callbackKey = 'onLinkIssueModelObjects';
    const callback = props.threeDCallbacks?.[callbackKey];

    if (!callback) {
      console.warn('No callback provided for link issue model objects');
      return;
    }

    const request: I3DActionRequest = {
      type: 'link-issue-model-objects',
      issueId: props.selectedIssue.id,
      contextId: props.contextId,
    };

    try {
      setThreeDActionLoading('link-issue-model-objects');
      setThreeDActionResult(null);

      // Get object link data from host
      const hostResponse = await callback(request);

      if (!hostResponse.success || !hostResponse.data) {
        throw new Error(
          hostResponse.message || 'Failed to get object link data from host'
        );
      }

      // Create object link using host response
      const linkData = {
        body: hostResponse.data, // Host provides complete CreateObjectLinkRequest
      };

      // Create the object link using API
      const createdLink = await createObjectLinkMutation.mutateAsync(linkData);

      // Show success feedback
      showSnackbar({
        text: 'Objects linked successfully',
        type: 'success',
        timeoutMilliseconds: 3000,
      });

      setThreeDActionResult({
        type: 'link-issue-model-objects',
        success: true,
        message: 'Objects linked successfully',
        data: { linkId: createdLink.id },
      });

      // Dispatch custom event for host communication
      const event = new CustomEvent('3d-action-completed', {
        detail: {
          request,
          response: {
            type: 'link-issue-model-objects',
            success: true,
            data: { linkId: createdLink.id },
          },
        },
      });
      document.dispatchEvent(event);
    } catch (error) {
      console.error('Link issue model objects failed:', error);

      // Show error feedback
      showSnackbar({
        text: 'Failed to link objects. Please try again.',
        type: 'error',
        timeoutMilliseconds: 4000,
      });

      setThreeDActionResult({
        type: 'link-issue-model-objects',
        success: false,
        message: `Failed to link objects: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setThreeDActionLoading(null);
    }
  };

  // Handle unlink issue model objects from host callback + API delete
  const handleUnlinkIssueModelObjects = async (): Promise<void> => {
    if (!props.selectedIssue?.id || !props.contextId) {
      console.warn('Cannot unlink issue model objects: missing required data');
      return;
    }

    const callbackKey = 'onUnlinkIssueModelObjects';
    const callback = props.threeDCallbacks?.[callbackKey];

    if (!callback) {
      console.warn('No callback provided for unlink issue model objects');
      return;
    }

    const request: I3DActionRequest = {
      type: 'unlink-issue-model-objects',
      issueId: props.selectedIssue.id,
      contextId: props.contextId,
    };

    try {
      setThreeDActionLoading('unlink-issue-model-objects');
      setThreeDActionResult(null);

      // Get object link deletion data from host
      const hostResponse = await callback(request);

      if (!hostResponse.success || !hostResponse.data) {
        throw new Error(
          hostResponse.message || 'Failed to get link deletion data from host'
        );
      }

      // Delete object link using host response
      const deleteLinkData = {
        path: hostResponse.data, // Host provides { linkId: string }
      };

      // Delete the object link using API
      await deleteObjectLinkMutation.mutateAsync(deleteLinkData);

      // Show success feedback
      showSnackbar({
        text: 'Objects unlinked successfully',
        type: 'success',
        timeoutMilliseconds: 3000,
      });

      setThreeDActionResult({
        type: 'unlink-issue-model-objects',
        success: true,
        message: 'Objects unlinked successfully',
      });

      // Dispatch custom event for host communication
      const event = new CustomEvent('3d-action-completed', {
        detail: {
          request,
          response: {
            type: 'unlink-issue-model-objects',
            success: true,
            data: { removed: true },
          },
        },
      });
      document.dispatchEvent(event);
    } catch (error) {
      console.error('Unlink issue model objects failed:', error);

      // Show error feedback
      showSnackbar({
        text: 'Failed to unlink objects. Please try again.',
        type: 'error',
        timeoutMilliseconds: 4000,
      });

      setThreeDActionResult({
        type: 'unlink-issue-model-objects',
        success: false,
        message: `Failed to unlink objects: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setThreeDActionLoading(null);
    }
  };

  const handleUnlinkAllIssueModelObjects = (): Promise<void> =>
    handle3DAction('unlink-all-issue-model-objects');

  // Handle add issue view from FAB button
  const handleAddIssueView = async (): Promise<void> => {
    if (!props.selectedIssue?.id || !props.contextId || !props.projectId) {
      console.warn('Cannot add issue view: missing required data');
      return;
    }

    const callbackKey = 'onAddIssueView';
    const callback = props.threeDCallbacks?.[callbackKey];

    if (!callback) {
      console.warn('No callback provided for add issue view');
      return;
    }

    const request: I3DActionRequest = {
      type: 'add-issue-view',
      issueId: props.selectedIssue.id,
      contextId: props.contextId,
    };

    try {
      setThreeDActionLoading('add-issue-view');
      setThreeDActionResult(null);

      // Get viewpoint data from host
      const hostResponse = await callback(request);

      if (!hostResponse.success || !hostResponse.data) {
        throw new Error(
          hostResponse.message || 'Failed to capture viewpoint from host'
        );
      }

      // Use view creation data directly from host response
      const viewData = {
        body: hostResponse.data, // Host provides complete CreateViewRequest
      };

      // Create the view using API
      const createdView = await createViewMutation.mutateAsync(viewData);

      // Store app-specific reserved data if config is provided
      if (props.reservedViewDataConfig) {
        try {
          await apiServices.viewApi.saveReservedViewData(
            createdView.id,
            props.reservedViewDataConfig.appName,
            props.reservedViewDataConfig.getReservedData({
              viewId: createdView.id,
              issueId: props.selectedIssue!.id,
            })
          );
        } catch (reservedError: unknown) {
          console.error(
            'Failed to save reserved view data for issue view',
            { viewId: createdView.id },
            reservedError
          );
        }
      }

      props.threeDCallbacks?.onIssueViewCreatedSuccess?.(
        createdView.id,
        props.selectedIssue!.id
      );

      // Show success feedback
      showSnackbar({
        text: 'View created successfully',
        type: 'success',
        timeoutMilliseconds: 3000,
      });

      setThreeDActionResult({
        type: 'add-issue-view',
        success: true,
        message: 'View created successfully',
        data: { viewId: createdView.id },
      });

      // Dispatch custom event for additional host communication
      const event = new CustomEvent('3d-action-completed', {
        detail: {
          request,
          response: {
            type: 'add-issue-view',
            success: true,
            data: { viewId: createdView.id },
          },
        },
      });
      document.dispatchEvent(event);
    } catch (error) {
      console.error('Add issue view failed:', error);
      props.threeDCallbacks?.onIssueViewCreateError?.(
        props.selectedIssue!.id,
        error instanceof Error ? error : new Error(String(error))
      );

      // Show error feedback
      showSnackbar({
        text: 'Failed to create view. Please try again.',
        type: 'error',
        timeoutMilliseconds: 4000,
      });

      setThreeDActionResult({
        type: 'add-issue-view',
        success: false,
        message: `Failed to create view: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setThreeDActionLoading(null);
    }
  };

  // Handle viewpoint capture for comments — returns the full CreateViewRequest
  // provided by the host so it can be saved to the backend after comment creation.
  const handleAddIssueCommentView =
    async (): Promise<CreateViewRequest | null> => {
      if (!props.selectedIssue?.id) {
        throw new Error('No selected issue for viewpoint capture');
      }

      const callbackKey = 'onAddIssueCommentView';
      const callback = props.threeDCallbacks?.[callbackKey];

      if (!callback) {
        throw new Error('No callback provided for viewpoint capture');
      }

      const request: I3DActionRequest = {
        type: 'add-issue-comment-view',
        issueId: props.selectedIssue.id,
        contextId: props.contextId,
      };

      try {
        const response = await callback(request);
        if (response && response.success && response.data) {
          return response.data as CreateViewRequest;
        }
        return null;
      } catch (error) {
        console.error('Viewpoint capture failed:', error);
        return null;
      }
    };

  const dispatchIssueViewSelect = async (
    viewId: string,
    reservedViewData?: Record<string, unknown>
  ): Promise<void> => {
    await handle3DAction('issue-view-select', {
      viewId,
      ...(reservedViewData && { reservedViewData }),
    });
  };

  const openIn360Viewer = async (viewId: string): Promise<boolean> => {
    const reservedViewData = reservedViewDataMap()[viewId];
    if (!reservedViewData) {
      return false;
    }

    if (!shouldRedirectMayaViewToProjectSight()) {
      await dispatchIssueViewSelect(viewId, reservedViewData.data);
      return true;
    }

    const deepLink = buildProjectSight360ViewerDeepLink(reservedViewData.data);
    if (!deepLink) {
      console.warn('[IssueRightPanel] Failed to build 360 viewer deep link', {
        viewId,
      });
      return false;
    }

    window.open(deepLink, '_blank', 'noopener,noreferrer');
    return true;
  };

  // Create a view from the captured request and attach it to the given comment.
  const handleCreateCommentView = async (
    commentId: string,
    viewRequest: CreateViewRequest
  ): Promise<void> => {
    const tcpsApiDomain = getTcpsApiBaseUrl();
    const client = createClient({ baseUrl: tcpsApiDomain, global: false });
    const accessToken = props.accessToken;
    client.interceptors.request.use((request) => {
      request.headers.set('Authorization', `Bearer ${accessToken}`);
      request.headers.set('Content-Type', 'application/json');
      return request;
    });

    const createdView = await createViewMutation.mutateAsync({
      body: viewRequest,
    });

    if (props.reservedViewDataConfig) {
      try {
        await apiServices.viewApi.saveReservedViewData(
          createdView.id,
          props.reservedViewDataConfig.appName,
          props.reservedViewDataConfig.getReservedData({
            viewId: createdView.id,
            issueId: props.selectedIssue!.id,
          })
        );
      } catch (reservedError: unknown) {
        console.error(
          'Failed to save reserved view data for comment view',
          { viewId: createdView.id },
          reservedError
        );
      }
    }

    const attachmentResponse = await postAddattachments({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      client: client as any,
      path: { commentId },
      body: [{ type: 'VIEW', id: createdView.id }],
    });

    if (attachmentResponse.error) {
      throw new Error('Failed to attach view to comment');
    }
  };

  // Handle view selection — fetches reserved data if config is provided, then delegates to consumer
  const handleIssueViewSelect = async (viewId: string): Promise<void> => {
    if (await openIn360Viewer(viewId)) {
      return;
    }

    let reservedViewData: Record<string, unknown> | undefined;

    if (props.reservedViewDataConfig?.appName) {
      try {
        const dto = await apiServices.viewApi.getReservedViewData(
          viewId,
          props.reservedViewDataConfig.appName
        );
        reservedViewData = dto?.data;
      } catch (error) {
        console.warn(
          '[IssueRightPanel] Failed to fetch reserved view data:',
          error
        );
      }
    }

    await dispatchIssueViewSelect(viewId, reservedViewData);
  };

  // Handle viewpoint deletion
  const handleDeleteViewpoint = async (viewpointId: string): Promise<void> => {
    try {
      await deleteViewMutation.mutateAsync({
        path: {
          viewId: viewpointId,
        },
      });
      props.threeDCallbacks?.onIssueViewDeletedSuccess?.(viewpointId);
    } catch (error) {
      console.error('Failed to delete viewpoint:', viewpointId, error);
      props.threeDCallbacks?.onIssueViewDeleteError?.(
        viewpointId,
        error instanceof Error ? error : new Error(String(error))
      );
      throw error;
    }
  };

  // Create mode renders static HTML form content

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TS6133: Reserved for future use
  const _handleUpdateIssue = (_formData: any): void => {
    // Handle issue update logic here
    props.onClose();
  };

  // Export dialog handlers
  const handleCloseExportDialog = (): void => {
    setShowExportDialog(false);
  };

  const handleToggleAssignedToMe = (): void => {
    setIsAssignedToMe(!isAssignedToMe());
  };

  const handleExportFilter = (newFilterData: ITFilterData): void => {
    setFilterData(newFilterData);
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TS6133: Reserved for future use
  const _handleExport = async (_payload: any): Promise<void> => {
    // TODO: Implement actual export API call
    // This would typically call an API service to create the export job
    try {
      // Simulate API call
      const SIMULATION_DELAY_MS = 1000;
      await new Promise((resolve) => setTimeout(resolve, SIMULATION_DELAY_MS));
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  };

  const handleCancelEdit = (): void => {
    // Reset form state
    resetFormAndTags();
    props.onClose();
  };

  // Invariant: this handler is only wired to the back button in edit mode
  // (the triggering `tc-icon-button` is rendered inside `<Show when={isEditMode()}>`),
  // so it can assume the panel is returning from edit → details. It intentionally
  // has no "create mode → close" branch: create mode uses `handleCancelEdit`
  // (via the cancel button) and the panel is unmounted rather than navigated.
  const handleBackButton = (): void => {
    props.onViewPortChange('displayDetailsPanel');
    resetFormAndTags();
  };

  const handleEdit = (): void => {
    props.onViewPortChange('displayEditPanel');

    // Populate form with selected issue data immediately
    if (props.selectedIssue) {
      populateFormWithIssueData(props.selectedIssue);
    }
  };

  // Delete handlers
  const handleDeleteClick = (): void => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!props.selectedIssue?.id || !props.contextId) {
      console.error('Cannot delete issue: missing issue ID or context ID');
      return;
    }

    setDeleteInProgress(true);

    try {
      await deleteIssueMutation.mutateAsync({
        url: '/context/{contextId}/issues/{issueId}' as const,
        path: {
          contextId: props.contextId,
          issueId: props.selectedIssue.id,
        },
      });

      // Show success notification
      showSnackbar({
        text: 'Issue deleted successfully',
        type: 'success',
        timeoutMilliseconds: 3000,
      });

      props.onIssueDeleted?.(props.selectedIssue.id);
      props.threeDCallbacks?.onIssueDeletedSuccess?.(props.selectedIssue.id);

      // Close the panel
      handleClose();
    } catch (error) {
      console.error('Failed to delete issue:', error);
      props.threeDCallbacks?.onIssueDeleteError?.(
        props.selectedIssue.id,
        error instanceof Error ? error : new Error(String(error))
      );

      // Show error notification
      showSnackbar({
        text: 'Failed to delete issue. Please try again.',
        type: 'error',
        timeoutMilliseconds: 5000,
      });
    } finally {
      setDeleteInProgress(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteCancel = (): void => {
    setShowDeleteConfirm(false);
  };

  const isCreateMode = (): boolean => props.viewPort === 'displayCreatePanel';
  const isEditMode = (): boolean => props.viewPort === 'displayEditPanel';
  const isReadMode = (): boolean => props.viewPort === 'displayDetailsPanel';

  // Reset form when switching to create mode
  createEffect(() => {
    if (isCreateMode()) {
      resetFormAndTags();
    }
  });

  // Auto-capture issue view preview when entering create mode
  createEffect(() => {
    if (
      isCreateMode() &&
      props.featureConfig?.issueViewPreview?.enabled &&
      props.threeDCallbacks?.onCaptureIssueViewPreview
    ) {
      const callback = props.threeDCallbacks.onCaptureIssueViewPreview;
      void callback().then((result) => setIssueViewPreviewData(result));
    }
  });

  // Reset form when selected issue changes (while in edit mode)
  createEffect(() => {
    // Watch for changes in selectedIssue
    const currentIssueId = props.selectedIssue?.id;
    // Only reset and populate when in edit mode and issue changes
    if (isEditMode() && currentIssueId && props.selectedIssue) {
      // Reset form when issue changes to ensure clean state
      resetFormAndTags();
      // Populate form with new issue data immediately
      populateFormWithIssueData(props.selectedIssue);
    }
  });

  // Populate form when switching to edit mode or when tags query data is available
  createEffect(() => {
    if (isEditMode() && props.selectedIssue) {
      populateFormWithIssueData(props.selectedIssue);
      setShowAddComment(false);
      setCommentData(undefined);
    }
  });

  // Create TanStack Form
  const issueForm = createForm(() => ({
    defaultValues: {
      title: '',
      description: '',
      assignees: [] as ITagItem[],
      followers: [] as ITagItem[],
      type: '',
      priority: '',
      dueDate: '',
      tags: [] as ITagItem[],
      status: '',
    },
    onSubmit: async ({ value }) => {
      if (!isCreateMode() && !isEditMode()) {
        console.warn('Form submission called in invalid mode');
        return;
      }

      if (!props.contextId) {
        console.error('Context ID not available for issue operation');
        return;
      }

      try {
        // Process form data for API
        const issueData = {
          url: '/context/{contextId}/issues' as const,
          path: { contextId: props.contextId },
          body: {
            title: value.title,
            description: value.description || '',
            ...(value.type &&
              value.type !== translate('TC.Common.SelectType') && {
                type: value.type,
              }),
            ...(value.priority &&
              value.priority !== translate('TC.Common.SelectPriority') && {
                priority: value.priority,
              }),
            ...(value.status &&
              value.status !== translate('TC.Common.SelectStatus') && {
                status: value.status,
              }),
            // Convert dueDate from ISO format (YYYY-MM-DD) to MM/DD/YYYY format for API
            ...(value.dueDate && {
              dueDate: formatDateToMMDDYYYY(value.dueDate),
            }),
            // Transform assignees from tag format to CURIE format (users:{tidUUID})
            assignees: assigneeTags()
              .map((tag) => {
                const [id, type, tiduuid] = tag.value.split('||');
                // API expects CURIE format for users
                if (type === 'USER' && tiduuid) {
                  return `users:${tiduuid}`;
                }
                // For groups, we might need a different format - check API docs
                if (type === 'GROUP' && id) {
                  return `groups:${id}`;
                }
                return null;
              })
              .filter((assignee) => assignee !== null) as string[], // Filter out invalid assignees
            // Include marker: new placement on create, or preserve existing on edit
            ...(props.pendingMarkerData
              ? {
                  marker: {
                    point: {
                      x: props.pendingMarkerData.point.x,
                      y: props.pendingMarkerData.point.y,
                      z: props.pendingMarkerData.point.z,
                    },
                  },
                }
              : isEditMode() && props.selectedIssue?.marker
                ? { marker: props.selectedIssue.marker }
                : {}),
          },
          // Request full marker field in response
          query: {
            fields: ['marker'],
          },
        };

        let result: IssueResponse | undefined;

        if (isCreateMode()) {
          // Call the issue creation mutation - only if mutation is available
          if (!createIssueMutation) {
            console.error('Issue API not ready yet');
            showSnackbar({
              text: 'Issue API not ready yet',
              type: 'error',
              timeoutMilliseconds: 4000,
            });
            return;
          }
          result = await createIssueMutation.mutateAsync(issueData);

          // Call onIssueCreated callback so the consumer can create IssueLinks
          if (
            props.featureConfig?.alwaysLinkOnCreate &&
            props.threeDCallbacks?.onIssueCreated &&
            result?.id &&
            props.contextId
          ) {
            try {
              const callbackResponse =
                await props.threeDCallbacks.onIssueCreated({
                  type: 'issue-created',
                  issueId: result.id,
                  contextId: props.contextId,
                });

              // Consumer returns IIssueCreatedData with appContext and contexts
              if (callbackResponse.success && callbackResponse.data) {
                const { appContext, contexts } = callbackResponse.data;

                // Validate required appContext properties to prevent malformed data
                if (
                  appContext &&
                  appContext.resourceId &&
                  appContext.appName &&
                  contexts &&
                  contexts.length > 0
                ) {
                  // Create IssueLinks for all location contexts
                  const linkCreationPromises = contexts.map(
                    (contextItem: Record<string, unknown>) => {
                      const contextData = JSON.stringify(contextItem);

                      const referenceData = {
                        contextId: props.contextId!,
                        issueId: result!.id,
                        reference: {
                          type: 'OBJECT' as const,
                          resourceId: appContext.resourceId,
                          description: `${appContext.appName} location`,
                          properties: {
                            contextData: contextData,
                          },
                        },
                      };

                      return addReferenceMutation.mutateAsync(referenceData);
                    }
                  );

                  // Create all links concurrently
                  await Promise.all(linkCreationPromises);
                }
              }
            } catch (error) {
              console.warn('Failed to create issue links:', error);
              props.reservedViewDataConfig?.onIssueLinkError?.(
                result!.id,
                error instanceof Error ? error : new Error(String(error))
              );
            }
          }

          // Auto-create view from preview if feature is enabled
          if (
            props.featureConfig?.issueViewPreview?.enabled &&
            isCreateMode() &&
            issueViewPreviewData() &&
            result?.id &&
            props.contextId
          ) {
            try {
              const preview = issueViewPreviewData()!;
              const createdView = await createViewMutation.mutateAsync({
                body: {
                  name: preview.title,
                  projectId: props.projectId,
                  issueId: result.id,
                  contextId: props.contextId,
                  models: [],
                  camera: preview.camera,
                  imageData: preview.imageData,
                },
              });
              if (props.reservedViewDataConfig) {
                await apiServices.viewApi.saveReservedViewData(
                  createdView.id,
                  props.reservedViewDataConfig.appName,
                  props.reservedViewDataConfig.getReservedData({
                    viewId: createdView.id,
                    issueId: result.id,
                  })
                );
              }
            } catch (viewError) {
              console.warn(
                'Failed to create issue view from preview:',
                viewError
              );
              props.reservedViewDataConfig?.onReservedDataError?.(
                '',
                viewError instanceof Error
                  ? viewError
                  : new Error(String(viewError))
              );
            }
          }

          if (result) {
            props.onIssueMutated?.(result);
            props.threeDCallbacks?.onIssueCreatedSuccess?.(result);
          }

          // Show success notification for issue creation
          showSnackbar({
            text: 'Issue created successfully',
            type: 'success',
            timeoutMilliseconds: 3000,
          });

          // Create comment if comment data exists
          const currentCommentData = commentData();
          if (currentCommentData && currentCommentData.comment.trim()) {
            try {
              // Create the comment for the new issue
              if (props.contextId && result.id) {
                await createCommentForIssue(
                  result.id,
                  currentCommentData,
                  props.projectId,
                  props.contextId
                );
              } else {
                throw new Error(
                  'Context ID and issue ID are required for comment creation'
                );
              }
            } catch (commentError) {
              console.error(
                'Failed to create comment for issue:',
                commentError
              );
              // Don't fail the whole operation if comment creation fails
              showSnackbar({
                text: 'Issue created but comment creation failed',
                type: 'warning',
                timeoutMilliseconds: 4000,
              });
            }
          }

          // Update followers after issue creation - only if there are followers to add
          const followers = followersTags();
          if (followers.length > 0 && result?.id && updateFollowersMutation) {
            try {
              // Transform followers from tag format to API expected format
              const followerIds = followers
                .map((tag) => {
                  const [id, type, tiduuid] = tag.value.split('||');
                  // API expects CURIE format for followers
                  if (type === 'USER' && tiduuid) {
                    return { followerId: `users:${tiduuid}` };
                  }
                  // For groups, we might need a different format
                  if (type === 'GROUP' && id) {
                    return { followerId: `groups:${id}` };
                  }
                  return null;
                })
                .filter((follower) => follower !== null) as Array<{
                followerId: string;
              }>;

              if (followerIds.length > 0) {
                await updateFollowersMutation.mutateAsync({
                  path: {
                    contextId: props.contextId!,
                  },
                  body: {
                    resourceId: `issues:${result.id}`,
                    followers: followerIds,
                  },
                });
              }
            } catch (followersError) {
              console.error(
                'Failed to update followers for new issue:',
                followersError
              );
              // Don't fail the whole operation if followers update fails
              // The issue was already created successfully
            }
          }
        } else if (isEditMode() && props.selectedIssue) {
          // Call the update issue mutation
          if (!updateIssueMutation) {
            console.error('Update issue API not ready yet');
            showSnackbar({
              text: 'Update issue API not ready yet',
              type: 'error',
              timeoutMilliseconds: 4000,
            });
            return;
          }

          const updateData = {
            url: '/context/{contextId}/issues/{issueId}' as const,
            path: {
              contextId: props.contextId!,
              issueId: props.selectedIssue.id,
            },
            body: issueData.body,
          };

          result = await updateIssueMutation.mutateAsync(updateData);

          // Show success notification for issue update
          showSnackbar({
            text: 'Issue updated successfully',
            type: 'success',
            timeoutMilliseconds: 3000,
          });

          if (result) {
            props.onIssueMutated?.(result);
            props.threeDCallbacks?.onIssueUpdatedSuccess?.(result);
          }

          // Handle followers update in edit mode - only if there are changes
          const currentFollowers = resourceFollowersQuery.data?.items || [];
          const formFollowers = followersTags();

          // Extract tiduuids from current followers (API format: users:{tiduuid})
          const currentFollowerIds = currentFollowers
            .map((follow) => {
              const USERS_PREFIX_LENGTH = 6;
              return follow.followerId?.startsWith('users:')
                ? follow.followerId.substring(USERS_PREFIX_LENGTH)
                : follow.followerId;
            })
            .filter(Boolean);

          // Extract tiduuids from form followers (tag format: {tiduuid}||USER||{tiduuid})
          const formFollowerIds = formFollowers
            .map((tag) => {
              const [_id, type, tiduuid] = tag.value.split('||');
              return type === 'USER' ? tiduuid : null;
            })
            .filter(Boolean);

          // Check if followers have changed
          const followersChanged =
            currentFollowerIds.length !== formFollowerIds.length ||
            !currentFollowerIds.every(
              (id) => id && formFollowerIds.includes(id)
            ) ||
            !formFollowerIds.every(
              (id) => id && currentFollowerIds.includes(id)
            );

          if (followersChanged && updateFollowersMutation) {
            try {
              // Transform followers from tag format to API expected format
              const followerIds = formFollowers
                .map((tag) => {
                  const [id, type, tiduuid] = tag.value.split('||');
                  // API expects CURIE format for followers
                  if (type === 'USER' && tiduuid) {
                    return { followerId: `users:${tiduuid}` };
                  }
                  // For groups, we might need a different format
                  if (type === 'GROUP' && id) {
                    return { followerId: `groups:${id}` };
                  }
                  return null;
                })
                .filter((follower) => follower !== null) as Array<{
                followerId: string;
              }>;

              await updateFollowersMutation.mutateAsync({
                path: {
                  contextId: props.contextId!,
                },
                body: {
                  resourceId: `issues:${props.selectedIssue.id}`,
                  followers: followerIds,
                },
              });
            } catch (followersError) {
              console.error(
                'Failed to update followers for edited issue:',
                followersError
              );
              // Don't fail the whole operation if followers update fails
              // The issue was already updated successfully
            }
          }

          // Create comment if comment data exists in edit mode
          const currentCommentData = commentData();
          if (currentCommentData && currentCommentData.comment.trim()) {
            try {
              // Create the comment for the edited issue
              if (props.contextId && props.selectedIssue.id) {
                await createCommentForIssue(
                  props.selectedIssue.id,
                  currentCommentData,
                  props.projectId,
                  props.contextId
                );
              } else {
                throw new Error(
                  'Context ID and issue ID are required for comment creation'
                );
              }
            } catch (commentError) {
              console.error(
                'Failed to create comment for edited issue:',
                commentError
              );
              // Don't fail the whole operation if comment creation fails
              showSnackbar({
                text: 'Issue updated but comment creation failed',
                type: 'warning',
                timeoutMilliseconds: 4000,
              });
            }
          }
        }

        // Handle form references - add them to the created/updated issue
        const currentFormReferences = formReferences();
        if (currentFormReferences.length > 0 && result?.id) {
          try {
            // Create references for the issue
            for (const reference of currentFormReferences) {
              try {
                // Remove temporary ID before creating the reference
                const { id: _id, ...referenceData } = reference;

                // Use the reference API service to add the reference
                await addReferenceMutation.mutateAsync({
                  contextId: props.contextId!,
                  issueId: result.id,
                  reference: referenceData,
                });
              } catch (referenceError) {
                console.error(
                  'Failed to add reference:',
                  reference.description,
                  referenceError
                );
                // Continue with other references even if one fails
              }
            }
          } catch (referencesError) {
            console.error(
              'Failed to process references for issue:',
              referencesError
            );
            // Don't fail the whole operation if reference processing fails
            // The issue was already created successfully
          }
        }

        // Handle tag associations - both new and existing tags
        const selectedTags = issueTags();
        if (selectedTags.length > 0 || (isEditMode() && result?.id)) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore TS6133: Reserved for future use
          const _mode = isCreateMode() ? 'new' : 'existing';

          try {
            // Get existing project tags for comparison
            const existingProjectTags = props.projectTags || [];
            const existingTagLabels = existingProjectTags.map((tag) =>
              tag.label.toLowerCase()
            );

            // Separate new tags from existing tags
            const newTags = selectedTags.filter(
              (tag) => !existingTagLabels.includes(tag.label.toLowerCase())
            );
            const existingTags = selectedTags.filter((tag) =>
              existingTagLabels.includes(tag.label.toLowerCase())
            );

            // For edit mode, we don't remove old associations to avoid breaking other issues
            // The API's removeObjectsFromTag removes ALL objects from a tag, not just this issue
            // So we only add new associations that don't already exist
            if (isEditMode() && result?.id) {
              // Edit mode: Only adding new tag associations, preserving existing ones
            }

            // Create new tags first
            const createdTagIds: string[] = [];
            if (newTags.length > 0 && createTagMutation) {
              for (const tag of newTags) {
                try {
                  const createdTag = await createTagMutation.mutateAsync({
                    body: {
                      label: tag.label,
                      projectId: props.projectId,
                      description: '', // Add description if needed
                      color: tag.color || '#000000', // Use tag color or default
                    },
                  });
                  if (createdTag?.id) {
                    createdTagIds.push(createdTag.id);
                  }
                } catch (createError) {
                  console.error(
                    'Failed to create tag:',
                    tag.label,
                    createError
                  );
                  // Continue with other tags even if one fails
                }
              }
            }

            // Associate all tags (both new and existing) with the issue
            if (addObjectsToTagMutation) {
              // Get IDs for existing tags
              const existingTagIds = existingTags
                .map((selectedTag) => {
                  const existingTag = existingProjectTags.find(
                    (projectTag) =>
                      projectTag.label.toLowerCase() ===
                      selectedTag.label.toLowerCase()
                  );
                  return existingTag?.id;
                })
                .filter((id) => id !== undefined) as string[];

              // Combine all tag IDs (newly created + existing)
              const allTagIds = [...createdTagIds, ...existingTagIds];

              // Associate all tags with the issue in parallel for better performance
              if (result?.id) {
                // eslint-disable-next-line solid/reactivity -- This function is used within an async callback and doesn't need to be reactive
                const tagAssociationPromises = allTagIds.map(async (tagId) => {
                  try {
                    await addObjectsToTagMutation.mutateAsync({
                      path: {
                        tagId: tagId,
                      },
                      body: [
                        {
                          id: result.id,
                          objectType: 'ISSUE',
                          contextId: props.contextId!,
                        },
                      ],
                    });
                    return { tagId, success: true };
                  } catch (associateError) {
                    console.error(
                      'Failed to associate tag',
                      tagId,
                      'with issue:',
                      associateError
                    );
                    return { tagId, success: false, error: associateError };
                  }
                });

                // Wait for all tag associations to complete
                const associationResults = await Promise.all(
                  tagAssociationPromises
                );

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore TS6133: Reserved for future use
                const _successfulAssociations = associationResults.filter(
                  (result) => result.success
                ).length;
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore TS6133: Reserved for future use
                const _failedAssociations = associationResults.filter(
                  (result) => !result.success
                ).length;
              } else {
                console.error('Issue ID not available for tag association');
              }
            }
          } catch (tagError) {
            console.error('Failed to process tags for new issue:', tagError);
            // Don't fail the whole operation if tag processing fails
            // The issue was already created successfully
          }
        }

        // Clear form state after successful submission
        resetFormAndTags();
        setShowAddComment(false);
        setCommentData(undefined);

        // Close the panel on success
        props.onClose();
      } catch (error) {
        console.error('Failed to create/update issue:', error);
        props.threeDCallbacks?.onIssueCreateOrUpdateError?.(
          error instanceof Error ? error : new Error(String(error))
        );

        // Show error notification based on mode
        const errorMessage = isCreateMode()
          ? 'Failed to create issue. Please try again.'
          : 'Failed to update issue. Please try again.';

        showSnackbar({
          text: errorMessage,
          type: 'error',
          timeoutMilliseconds: 5000,
        });
      }
    },
  }));

  const renderCreateForm = (): JSX.Element => (
    <div class="panel-body">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Form submission is now only triggered explicitly via footer buttons
        }}
      >
        <div class="sub-section">
          <div class="section-details">
            <div class="input-group">
              {/* Title Field */}
              <issueForm.Field
                name="title"
                validators={{
                  onChange: ({ value }) =>
                    !value
                      ? 'A title is required'
                      : value.length > 255 // eslint-disable-line no-magic-numbers -- 255 is the standard character limit for titles
                        ? 'Title must be below 255 characters'
                        : undefined,
                }}
                children={(field) => (
                  <>
                    <tc-custom-input
                      name="title"
                      label={translate('TC.Common.Title')}
                      class="input-group"
                      data-cy="title"
                      type="text"
                      placeholder={translate('TC.Common.Title')}
                      value={field().state.value}
                      touched={field().state.meta.isTouched}
                      error={
                        field().state.meta.isTouched &&
                        field().state.meta.errors.length > 0
                          ? field().state.meta.errors[0]
                          : undefined
                      }
                      onBlur={field().handleBlur}
                      onInput={(e: InputEvent) =>
                        field().handleChange(
                          (e.target as HTMLInputElement).value
                        )
                      }
                    />
                  </>
                )}
              />
            </div>
          </div>
        </div>

        <Show
          when={
            props.featureConfig?.issueViewPreview?.enabled && isCreateMode()
          }
        >
          <div class="sub-section">
            <div class="section-details">
              <div style={{ position: 'relative' }}>
                <img
                  src={issueViewPreviewData()?.imageData}
                  alt="View preview — capturing..."
                  style="width:100%;border-radius:4px;border:1px solid var(--modus-wc-color-base-200,#e0e1e9);min-height:120px;object-fit:cover;background:var(--modus-wc-color-base-200,#e0e1e9)"
                />
                <button
                  type="button"
                  style="position:absolute;top:8px;right:8px;background:rgba(0,0,0,0.5);color:#fff;border:none;border-radius:4px;padding:4px 8px;cursor:pointer;font-size:12px"
                  onClick={() => {
                    const callback =
                      props.threeDCallbacks?.onCaptureIssueViewPreview;
                    if (callback) {
                      void callback().then((result) =>
                        setIssueViewPreviewData(result)
                      );
                    }
                  }}
                >
                  Recapture
                </button>
              </div>
            </div>
          </div>
        </Show>

        <div class="sub-section">
          <div class="section-details">
            {/* Type Field */}
            <issueForm.Field
              name="type"
              children={(field) => (
                <>
                  {/* @ts-expect-error -- tc-custom-select is a custom web component not recognized by TypeScript */}
                  <tc-custom-select
                    name="type"
                    label={translate('TC.Common.Type')}
                    options={getTypeOptions()}
                    getValue={(v: unknown) => v as string}
                    getDisplayValue={(v: unknown) => v as string}
                    defaultValue={translate('TC.Common.SelectType')}
                    data-cy="type"
                    value={field().state.value}
                    on:tc-customselect-change={(event: CustomEvent) =>
                      field().handleChange(event.detail.value)
                    }
                  />
                </>
              )}
            />
          </div>
        </div>

        <div class="sub-section">
          <div class="section-details">
            {/* Priority Field */}
            <issueForm.Field
              name="priority"
              children={(field) => (
                <>
                  {/* @ts-expect-error -- tc-custom-select is a custom web component not recognized by TypeScript */}
                  <tc-custom-select
                    name="priority"
                    label={translate('TC.Common.Priority')}
                    options={getPriorityOptions()}
                    getValue={(v: unknown) => v as string}
                    getDisplayValue={(v: unknown) => v as string}
                    defaultValue={translate('TC.Common.SelectPriority')}
                    data-cy="priority"
                    style={{ zIndex: 1000 }}
                    value={field().state.value}
                    on:tc-customselect-change={(event: CustomEvent) =>
                      field().handleChange(event.detail.value)
                    }
                  />
                </>
              )}
            />
          </div>
        </div>

        <div class="sub-section py-0">
          <div class="section-details">
            <div class="input-group">
              {/* Status Field */}
              <issueForm.Field
                name="status"
                children={(field) => (
                  <>
                    {/* @ts-expect-error -- tc-custom-select is a custom web component not recognized by TypeScript */}
                    <tc-custom-select
                      name="status"
                      label={translate('TC.Common.Status')}
                      options={getStatusOptions()}
                      getValue={(v: unknown) => v as string}
                      getDisplayValue={(v: unknown) => v as string}
                      defaultValue={translate('TC.Common.SelectStatus')}
                      data-cy="status"
                      value={field().state.value}
                      on:tc-customselect-change={(event: CustomEvent) =>
                        field().handleChange(event.detail.value)
                      }
                    />
                  </>
                )}
              />
            </div>
          </div>
        </div>

        <div class="sub-section">
          <div class="section-details">
            {/* Due Date Field */}
            <issueForm.Field
              name="dueDate"
              children={(field) => (
                <>
                  {/* @ts-expect-error -- tc-custom-datepicker is a custom web component not recognized by TypeScript */}
                  <tc-custom-datepicker
                    name="dueDate"
                    label={translate('TC.Common.DueDate')}
                    data-cy="dueDate"
                    placeholder={translate('TC.Common.SelectDate')}
                    value={field().state.value}
                    touched={field().state.meta.isTouched}
                    error={
                      field().state.meta.isTouched &&
                      field().state.meta.errors.length > 0
                        ? field().state.meta.errors[0]
                        : undefined
                    }
                    on:tc-customdatepicker-input={(e: CustomEvent) => {
                      field().handleChange(e.detail.value);
                    }}
                    onBlur={field().handleBlur}
                  />
                </>
              )}
            />
          </div>
        </div>

        <div class="sub-section">
          <div class="section-details">
            {/* Description Field */}
            <issueForm.Field
              name="description"
              validators={{
                onChange: ({ value }) =>
                  value && value.length > 1024 // eslint-disable-line no-magic-numbers -- 1024 is the standard character limit for descriptions
                    ? 'Description must be below 1024 characters'
                    : undefined,
              }}
              children={(field) => (
                <>
                  {/* @ts-expect-error -- tc-custom-textarea is a custom web component not recognized by TypeScript */}
                  <tc-custom-textarea
                    name="description"
                    label={translate('TC.Common.Description')}
                    data-cy="description"
                    placeholder={translate('TC.Common.Description')}
                    value={field().state.value}
                    touched={field().state.meta.isTouched}
                    error={
                      field().state.meta.isTouched &&
                      field().state.meta.errors.length > 0
                        ? field().state.meta.errors[0]
                        : undefined
                    }
                    onInput={(e: InputEvent) =>
                      field().handleChange(
                        (e.target as HTMLTextAreaElement).value
                      )
                    }
                    onBlur={field().handleBlur}
                  />
                  {/* Show error message while typing without red styling */}
                  <Show
                    when={
                      !field().state.meta.isTouched &&
                      field().state.meta.errors.length > 0
                    }
                  >
                    <div
                      class="text-small"
                      style={{
                        color: '#d32f2f',
                        // eslint-disable-next-line @typescript-eslint/naming-convention -- margin-top is a CSS property name
                        'margin-top': '4px',
                      }}
                    >
                      {field().state.meta.errors[0]}
                    </div>
                  </Show>
                </>
              )}
            />
          </div>
        </div>

        <div class="sub-section">
          <div class="section-details">
            {/* Assignee Field */}
            <issueForm.Field
              name="assignees"
              children={(field) => (
                <>
                  {/* @ts-expect-error -- tc-tag-editor is a custom web component not recognized by TypeScript */}
                  <tc-tag-editor
                    title={translate('assignee')}
                    place-holder={translate('TC.Common.BeginTypingToAdd')}
                    enable-search-icon={false}
                    allow-custom-tag={false}
                    separator={[Separators.ENTER]}
                    selected-tag-list={assigneeTags()}
                    tag-list={props.allAssignees}
                    on:tag-change={(event: CustomEvent | ITagItem[]) => {
                      handleAssigneeTagChange(event);
                      field().handleChange(assigneeTags());
                    }}
                  />
                </>
              )}
            />
          </div>
        </div>

        <Show when={!configuration.isIssueAclEnabled()}>
          <div class="sub-section">
            <div class="section-details">
              <issueForm.Field
                name="followers"
                children={(field) => (
                  <>
                    {/* @ts-expect-error -- tc-tag-editor is a custom web component not recognized by TypeScript */}
                    <tc-tag-editor
                      title={translate('TC.Topic.Followers')}
                      place-holder={translate('TC.Common.BeginTypingToAdd')}
                      enable-search-icon={false}
                      allow-custom-tag={false}
                      separator={[Separators.ENTER]}
                      selected-tag-list={followersTags()}
                      tag-list={props.allAssignees}
                      on:tag-change={(event: CustomEvent | ITagItem[]) => {
                        handleFollowersTagChange(event);
                        field().handleChange(followersTags());
                      }}
                    />
                  </>
                )}
              />
            </div>
          </div>
        </Show>

        <div class="sub-section">
          <div class="section-details">
            {/* Tags Field */}
            <issueForm.Field
              name="tags"
              children={(field) => (
                <>
                  {/* @ts-expect-error -- tc-tag-editor is a custom web component not recognized by TypeScript */}
                  <tc-tag-editor
                    title={translate('tags')}
                    place-holder={translate('beginTypeToSearch')}
                    enable-search-icon={false}
                    allow-custom-tag={true}
                    separator={[Separators.ENTER]}
                    selected-tag-list={issueTags()}
                    tag-list={props.projectTags}
                    on:tag-change={(event: CustomEvent | ITagItem[]) => {
                      handleIssueTagChange(event);
                      field().handleChange(issueTags());
                    }}
                  />
                </>
              )}
            />
          </div>
        </div>

        <div class="px-2">
          <button
            class="button link-primary inline-right"
            type="button"
            data-cy="add-reference-link"
            onClick={handleFormAddReference}
          >
            Add references
          </button>
        </div>

        {/* Form References List */}
        <Show when={formReferences().length > 0}>
          <div class="sub-section">
            <div class="reference-header">
              <h6 class="pl-2">
                {translate('TC.Topic.References')} ({formReferences().length})
              </h6>
              <div class="flex-row ml-auto">
                {/* @ts-expect-error -- tc-icon-button is a custom web component not recognized by TypeScript */}
                <tc-icon-button
                  size="icon-medium"
                  color="tertiary"
                  icon="tc-icon-content-add"
                  onClick={handleFormAddReference}
                />
                {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
                <tc-dropdown-menu
                  anchor-icon="icon-font tc-icon-more-vert"
                  drop-down-options={['Remove All']}
                  position="right"
                  class="mr-0"
                  on:selection-changed={(event: CustomEvent) => {
                    if (event.detail.selected === 'Remove All') {
                      setFormReferences([]);
                    }
                  }}
                />
              </div>
            </div>
            <div class="section-details pr-0">
              <ul class="list">
                <For each={formReferences()}>
                  {(reference) => (
                    <li class="list-item group">
                      <div class="group-items px-0 pointer">
                        {/* Reference icon based on type */}
                        <Show
                          when={reference.type === 'FILE'}
                          fallback={<i class="icon-font i16 tc-icon-link" />}
                        >
                          <i class="icon-font i16 tc-icon-file" />
                        </Show>

                        {/* Reference details */}
                        <div class="file-list-item reference-item">
                          <div class="block">
                            <Show
                              when={reference.type === 'URL' && reference.url}
                              fallback={
                                <span
                                  class="text-ellipsis"
                                  data-cy={reference.description}
                                >
                                  {reference.description}
                                </span>
                              }
                            >
                              <a
                                class="link-secondary"
                                href={reference.url}
                                target="_blank"
                                rel="noreferrer"
                                data-cy={reference.description}
                              >
                                {reference.description}
                              </a>
                            </Show>
                          </div>
                        </div>

                        {/* Remove button */}
                        {/* @ts-expect-error -- tc-icon-button is a custom web component not recognized by TypeScript */}
                        <tc-icon-button
                          icon="tc-icon-content-close"
                          size="icon-small"
                          color="tertiary"
                          class="ml-auto"
                          onClick={() =>
                            handleFormReferenceRemove(reference.id!)
                          }
                          classes="icon-margin"
                        />
                      </div>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </div>
        </Show>

        <div class="px-2">
          <button
            class="button link-primary inline-right"
            type="button"
            data-cy="add-comment-link"
            onClick={() => {
              if (showAddComment()) {
                removeComment();
              }
              setShowAddComment(!showAddComment());
            }}
          >
            {showAddComment()
              ? translate('TC.Common.Remove')
              : translate('addComment')}
          </button>
        </div>

        {/* Comment Section */}
        <Show when={showAddComment()}>
          <CommentProvider
            accessToken={props.accessToken}
            currentUser={props.currentUser}
            projectId={props.projectId}
            issueId={
              isEditMode()
                ? props.selectedIssue?.id || 'temp-issue-id'
                : 'temp-issue-id'
            } // Use actual issue ID in edit mode
            apiBaseUrl={getTcpsApiBaseUrl()}
          >
            <AddComment
              projectId={props.projectId}
              objectId={isEditMode() ? props.selectedIssue?.id : undefined} // Use actual issue ID in edit mode
              onCommentAdded={handleCommentAdded}
              onCommentDataChange={handleCommentDataChange}
              maxHeight={200}
              userTiduuid={props.currentUser.tiduuid}
              embedIn3D={false}
              refreshViewpointFn={noOpRefreshViewpointFn}
              objectType="ISSUE"
              contextId={props.contextId}
              isEditMode={true}
              shouldEmitData={isCreateMode() || isEditMode()} // Emit data in create/edit modes
              onAddIssueCommentView={handleAddIssueCommentView}
              onCreateCommentView={handleCreateCommentView}
            />
          </CommentProvider>
        </Show>

        {/* Form AttachmentPicker Dialog */}
        <Show when={showFormAttachmentPicker()}>
          <AttachmentPicker
            addAttachment={handleFormAttachmentPickerSubmit}
            onClose={handleFormAttachmentPickerClose}
            allowedAttachmentTypes="FILE_FROM_CONNECT,FILE_FROM_COMPUTER,LINK"
            projectId={props.projectId}
            accessToken={props.accessToken}
            tcpsApiDomain={getTcpsApiBaseUrl()}
            customTitle={translate('TC.Topic.AddReferences')}
            customButtonName={translate('TC.Topic.AddReferences')}
            disableMultiSelect={false}
            projectName={props.project?.name || 'Project'}
            is3DEmbbedded={true}
            inProgressFlag={false}
          />
        </Show>
      </form>
    </div>
  );

  const issueActions = (): readonly string[] | undefined =>
    props.selectedIssue?._actions;

  // Supports both new ('issue:Update') and legacy ('ISSUE_UPDATE') action formats
  const canEditIssue = (): boolean => {
    const actions = issueActions();
    if (!actions || !Array.isArray(actions)) return false;
    return actions.includes('issue:Update') || actions.includes('ISSUE_UPDATE');
  };

  const canUpdatePermissions = createMemo(() =>
    canUpdatePermissionsFromActions(
      issueActions(),
      configuration.isIssueAclEnabled()
    )
  );

  const canDeleteIssue = (): boolean =>
    canDeleteIssueFromActions(issueActions());

  const handleOpenSharingModal = (): void => {
    setShowSharingModal(true);
  };

  const handleSharingModalClose = (): void => {
    setShowSharingModal(false);
  };

  const handleSharingModalSave = (): void => {
    setShowSharingModal(false);
    props.onClose();
    void queryClient.invalidateQueries({ queryKey: ['issues'] });
    if (props.selectedIssue) {
      props.onIssueMutated?.(props.selectedIssue);
    }
  };

  const detailPanelRead = (): JSX.Element => {
    const dropdownOptions = (): string[] => {
      const options = [translate('exportLabel')];
      if (configuration.isIssueAclEnabled() && canDeleteIssue()) {
        options.push(translate('TC.Common.Delete'));
      }
      return options;
    };
    return (
      <>
        <div class="panel-title">
          <h3 class="h3 no-ellipsis" />
        </div>
        <div class="row-distribute full-width">
          <div class="flex-row">
            {/* Navigation buttons */}
            <tc-custom-tooltip
              text={translate('TC.Common.Previous')}
              position="bottom"
              arrow={true}
              notDynamic={true}
            >
              {/* @ts-expect-error -- tc-icon-button is a custom web component not recognized by TypeScript */}
              <tc-icon-button
                icon="tc-icon-chevron-left"
                size="icon-medium"
                color="tertiary"
                disabled={!canNavigatePrevious()}
                onClick={handleNavigatePrevious}
              />
            </tc-custom-tooltip>
            <tc-custom-tooltip
              text={translate('TC.Common.Next')}
              position="bottom"
              arrow={true}
              notDynamic={true}
            >
              {/* @ts-expect-error -- tc-icon-button is a custom web component not recognized by TypeScript */}
              <tc-icon-button
                icon="tc-icon-chevron-right"
                size="icon-medium"
                color="tertiary"
                disabled={!canNavigateNext()}
                onClick={handleNavigateNext}
              />
            </tc-custom-tooltip>
          </div>
          <div class="flex-row">
            {/* Show follow button if user is not following, unfollow button if they are */}
            <Show
              when={isCurrentUserFollowing()}
              fallback={
                /* Follow button - shown when not following */
                <>
                  <tc-custom-tooltip
                    text={
                      followResourceMutation.isPending
                        ? 'Following...'
                        : 'Follow this issue'
                    }
                    position="bottom"
                    arrow={true}
                    notDynamic={true}
                    disableToolTip={followResourceMutation.isPending}
                  >
                    {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
                    <tc-icon-button
                      icon="tc-icon-user-add"
                      size="icon-medium"
                      color="tertiary"
                      onClick={handleFollow}
                      disabled={followResourceMutation.isPending}
                    />
                  </tc-custom-tooltip>
                </>
              }
            >
              {/* Unfollow button - shown when already following */}
              <tc-custom-tooltip
                text={
                  unfollowResourceMutation.isPending
                    ? 'Unfollowing...'
                    : 'Unfollow this issue'
                }
                position="bottom"
                arrow={true}
                notDynamic={true}
                disableToolTip={unfollowResourceMutation.isPending}
              >
                {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
                <tc-icon-button
                  icon="tc-icon-user-remove"
                  size="icon-medium"
                  color="tertiary"
                  onClick={handleUnfollow}
                  disabled={unfollowResourceMutation.isPending}
                />
              </tc-custom-tooltip>
            </Show>
            <tc-custom-tooltip
              text={
                resourceFollowersQuery.isLoading || issueTagsQuery()?.isLoading
                  ? 'Loading data...'
                  : !canEditIssue()
                    ? translate('TC.Common.PermissionDeniedWarning')
                    : translate('TC.Common.Edit')
              }
              position="bottom"
              arrow={true}
              notDynamic={true}
              disableToolTip={
                !canEditIssue() ||
                resourceFollowersQuery.isLoading ||
                issueTagsQuery()?.isLoading
              }
            >
              {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
              <tc-icon-button
                icon="tc-icon-edit"
                size="icon-medium"
                color="tertiary"
                disabled={
                  resourceFollowersQuery.isLoading ||
                  issueTagsQuery()?.isLoading ||
                  !canEditIssue()
                }
                onClick={handleEdit}
              />
            </tc-custom-tooltip>
            <Show
              when={configuration.isIssueAclEnabled()}
              fallback={
                <Show when={canDeleteIssue()}>
                  <tc-custom-tooltip
                    text={translate('TC.Topic.DeleteTopic')}
                    position="bottom"
                    arrow={true}
                    notDynamic={true}
                  >
                    {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
                    <tc-icon-button
                      icon="tc-icon-delete"
                      size="icon-medium"
                      color="tertiary"
                      onClick={handleDeleteClick}
                    />
                  </tc-custom-tooltip>
                </Show>
              }
            >
              <tc-custom-tooltip
                text={
                  canUpdatePermissions()
                    ? translate('TC.Common.SharedWith') || 'Share'
                    : translate('TC.Common.PermissionDeniedWarning')
                }
                position="bottom"
                arrow={true}
                notDynamic={true}
              >
                {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
                <tc-icon-button
                  icon="tc-icon-user-permissions"
                  size="icon-medium"
                  color="tertiary"
                  disabled={!canUpdatePermissions()}
                  onClick={handleOpenSharingModal}
                />
              </tc-custom-tooltip>
            </Show>
            <tc-custom-tooltip
              text={translate('TC.Common.MoreOptions')}
              position="bottom"
              arrow={true}
              notDynamic={true}
            >
              {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
              <tc-dropdown-menu
                type="icon"
                anchor-icon="icon-font tc-icon-more-vert"
                anchor-class-name="icon-medium tertiary icon-circle"
                drop-down-options={dropdownOptions()}
                position="right"
                style={{ zIndex: 1000 }}
                on:selection-changed={(event: CustomEvent) => {
                  const selectedOption = event.detail.selected;
                  if (selectedOption === translate('exportLabel')) {
                    setShowExportDialog(true);
                  } else if (selectedOption === translate('TC.Common.Delete')) {
                    handleDeleteClick();
                  }
                }}
              />
            </tc-custom-tooltip>
            <tc-custom-tooltip
              text={translate('TC.Common.Close')}
              position="bottom"
              arrow={true}
              notDynamic={true}
            >
              {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
              <tc-icon-button
                icon="tc-icon-content-close"
                size="icon-medium"
                color="tertiary"
                onClick={handleClose}
              />
            </tc-custom-tooltip>
          </div>
        </div>
      </>
    );
  };

  const detailPanelCreateEdit = (title: string): JSX.Element => {
    return (
      <>
        <div class="flex-row full-width">
          <div class="flex-row align-items-center" style={{ width: 'inherit' }}>
            <Show when={isEditMode()}>
              {/* Simple icon buttons as web components (no handlers for now) */}
              {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
              <tc-icon-button
                icon="tc-icon-arrow-line-back"
                size="icon-medium"
                color="tertiary"
                onClick={handleBackButton}
              />
            </Show>
            <h3 class="h3 no-ellipsis">{title}</h3>
          </div>
          <div class="topic-panel-header">
            {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
            <tc-icon-button
              icon="icon-font tc-icon-content-close"
              size="icon-medium"
              color="tertiary"
              onClick={handleClose}
            />
          </div>
        </div>
      </>
    );
  };

  // Single Issue Details Section
  const singleIssueDetails = (): JSX.Element => (
    <div class="sub-section mt-2">
      <div class="section-details">
        {props.selectedIssue?.serverAssignedId && (
          <h6 class="text-blue issue-id-text">
            {props.selectedIssue.serverAssignedId}
          </h6>
        )}
        <div class="flex items-center justify-between">
          {props.selectedIssue?.title && (
            <h3 class="mb-0 mr-2 issue-title-text">
              {props.selectedIssue.title}
            </h3>
          )}
          <ul class="flex-row-wrap">
            <li
              class="chips non-editable small"
              style={`background-color: ${getStatusColor(props.configurationData ?? null, props.selectedIssue?.status || 'Waiting')}; color: ${getStatusTextColor(props.configurationData ?? null, props.selectedIssue?.status || 'Waiting')};`}
            >
              <span>
                {getStatusTranslation(props.selectedIssue?.status || 'Waiting')}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );

  // Single Issue Coordination Section
  const singleIssueCoordination = (): JSX.Element => (
    <div
      class={`sub-section ${
        coordinationCollapse() ? 'coordination-border' : ''
      }`}
    >
      <div
        class="section-header group pointer"
        onClick={() => setCoordinationCollapse(!coordinationCollapse())}
      >
        <h5 class="section-header-title">
          {translate('TC.Topic.Coordination')}
        </h5>
        <button class="button icon-circle tertiary icon-medium">
          <i
            class={`icon-font ${
              coordinationCollapse()
                ? 'tc-icon-chevron-down'
                : 'tc-icon-chevron-up'
            }`}
          />
        </button>
      </div>
      <Show when={!coordinationCollapse()}>
        <div class="section-details">
          <div class="label-group">
            <label>{translate('TC.Common.AssignedTo')}</label>
            <div class="flex-row-wrap pt-1">
              <Show
                when={
                  props.selectedIssue?.assignees &&
                  props.selectedIssue.assignees.length > 0
                }
                fallback={
                  <li class="chips non-editable rectangle">
                    {translate('TC.Common.Unassigned')}
                  </li>
                }
              >
                <For each={props.selectedIssue?.assignees}>
                  {(assignee: string) => {
                    const userName = getUserName(
                      assignee,
                      props.projectUsersData
                    );
                    return (
                      <li class="chips non-editable rectangle">
                        <span class="truncate">{userName}</span>
                      </li>
                    );
                  }}
                </For>
              </Show>
            </div>
          </div>

          <Show
            when={
              resourceFollowersQuery.data?.items &&
              resourceFollowersQuery.data.items.length > 0
            }
          >
            <div class="label-group">
              <label>{translate('TC.Topic.Followers')}</label>
              <div>
                <ul class="flex-row-wrap pt-1">
                  <Show
                    when={resourceFollowersQuery.isLoading}
                    fallback={
                      <For each={resourceFollowersQuery.data?.items}>
                        {(follow) => {
                          // Extract tiduuid from followerId format "users:tiduuid"
                          const USERS_PREFIX = 'users:';
                          const USERS_PREFIX_LENGTH = USERS_PREFIX.length;
                          const tiduuid = follow.followerId?.startsWith(
                            USERS_PREFIX
                          )
                            ? follow.followerId.substring(USERS_PREFIX_LENGTH)
                            : follow.followerId;

                          if (!tiduuid) {
                            return (
                              <li class="chips non-editable rectangle">
                                Unknown User
                              </li>
                            );
                          }

                          const userName = getUserName(
                            tiduuid,
                            props.projectUsersData
                          );
                          return (
                            <li class="chips non-editable rectangle">
                              <span class="truncate">{userName}</span>
                            </li>
                          );
                        }}
                      </For>
                    }
                  >
                    <li class="chips non-editable rectangle">
                      Loading followers...
                    </li>
                  </Show>
                </ul>
              </div>
            </div>
          </Show>

          <Show
            when={
              configuration.isIssueAclEnabled() &&
              isSharedWithLoaded() &&
              sharedWithChips().length > 0
            }
          >
            <div class="label-group">
              <label>{translate('TC.Common.SharedWith')}</label>
              <div class="pt-1 shared-with-list">
                <For each={sharedWithChips()}>
                  {(chip) => <div class="shared-with-entry">{chip}</div>}
                </For>
              </div>
            </div>
          </Show>

          <div class="label-group">
            <label>{translate('TC.Topic.Author')}</label>
            <div>
              <ul class="flex-row-wrap pt-1">
                <li class="chips non-editable rectangle">
                  {getUserName(
                    props.selectedIssue?.createdBy,
                    props.projectUsersData
                  )}
                </li>
              </ul>
            </div>
          </div>

          <Show when={(issueTagsQuery()?.data?.length ?? 0) > 0}>
            <div class="label-group">
              <label>{translate('Tags.Tags')}</label>
              <div>
                <ul class="flex-row-wrap pt-1">
                  <Show
                    when={issueTagsQuery()?.isLoading}
                    fallback={
                      <For each={issueTagsQuery()?.data}>
                        {(tag: any) => (
                          <li class="chips non-editable rectangle">
                            {tag.label || tag.name}
                          </li>
                        )}
                      </For>
                    }
                  >
                    <li class="chips non-editable rectangle">
                      Loading tags...
                    </li>
                  </Show>
                </ul>
              </div>
            </div>
          </Show>

          <div class="row-center">
            <span class="text pt-1">
              <small class="small-text">
                Created{' '}
                {props.selectedIssue?.createdAt
                  ? new Date(props.selectedIssue.createdAt).toLocaleDateString()
                  : 'Unknown date'}
              </small>
            </span>
          </div>
          <div class="row-center">
            <span class="text pt-1">
              <small class="small-text">
                Modified{' '}
                {props.selectedIssue?.updatedAt
                  ? new Date(props.selectedIssue.updatedAt).toLocaleDateString()
                  : 'Unknown date'}{' '}
                by{' '}
                {getUserName(
                  props.selectedIssue?.updatedBy,
                  props.projectUsersData
                )}
              </small>
            </span>
          </div>
        </div>
      </Show>
    </div>
  );

  // Views Section
  const singleViewsAttachments = (): JSX.Element => {
    // Transform API data to UI format
    const viewpointList = createMemo(() => {
      if (viewsQuery.isLoading || viewsQuery.isError || !viewsQuery.data) {
        return [];
      }

      return viewsQuery.data.map((view) => ({
        // eslint-disable-next-line @typescript-eslint/naming-convention -- API response property name
        view_id: view.id,
        name: view.name,
        thumbnail:
          view.thumbnail ||
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==',
        // eslint-disable-next-line @typescript-eslint/naming-convention -- API response property name
        created_at: view.createdOn,
        author: view.createdBy
          ? `${view.createdBy.firstName} ${view.createdBy.lastName}`
          : 'Unknown',
        guid: view.id,
      }));
    });

    const viewpointErrorFlag = createMemo(() => {
      return viewsQuery.isError || false;
    });

    const getViewpointDropDownOptions = (viewId: string): string[] => {
      return getIssueViewpointDropDownOptions(
        isElementHidden('load-original-versions-option'),
        shouldShowMayaReservedViewRoutingAction(
          Boolean(reservedViewDataMap()[viewId]),
          props.featureConfig
        )
      );
    };

    // Preview state is handled at component level

    // Update viewpoint count based on actual data
    createEffect(() => {
      setViewpointCount(viewpointList().length);
    });

    return (
      <div class="sub-section">
        <div class="reference-header">
          <div class="px-2">
            <h6 class="section-header-title">{translate('TC.Common.Views')}</h6>
          </div>
        </div>
        <Show
          when={viewpointList().length === 0 && !viewpointErrorFlag()}
          fallback={
            <div
              class={`section-details ${!viewpointErrorFlag() ? 'pt-1' : ''}`}
            >
              <ul
                class="list flex-row-wrap"
                data-testid="issue-management-views-list"
              >
                {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
                <tc-limit-visible-items
                  show-more={translate('TC.Common.ShowAll')}
                  class="list flex-row-wrap"
                  item-limit={VIEWPOINT_ITEM_LIMIT}
                  content={viewpointList().map(
                    (viewPoint: any, index: number) => (
                      <li
                        class="list-item group mr-1 pb-1"
                        onClick={() => {
                          void handleIssueViewSelect(viewPoint.guid);
                        }}
                      >
                        <div
                          class="viewpoint-container"
                          style={{ height: '75px' }}
                        >
                          <ViewThumbnail
                            view={{
                              id: viewPoint.guid,
                              name: viewPoint.name,
                              thumbnail: viewPoint.thumbnail,
                              createdOn: viewPoint.created_at,
                              createdBy:
                                viewPoint.author !== 'Unknown'
                                  ? {
                                      firstName:
                                        viewPoint.author.split(' ')[0] || '',
                                      lastName:
                                        viewPoint.author.split(' ')[1] || '',
                                    }
                                  : undefined,
                            }}
                            accessToken={props.accessToken}
                          />
                          {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
                          <tc-dropdown-menu
                            class="viewpoint bg-gray_7 mr-0"
                            style="width: 25px; height: 25px; position: relative; z-index: 1000; right: -51px; bottom: 29px; display: flex; align-items: center; border: none;"
                            custom-position-class={`dropdown-centered-absolute-${(() => {
                              const classes = ['first', 'second', 'third'];
                              return classes[index % VIEWPOINT_CLASSES_COUNT];
                            })()}`}
                            anchor-class-name="icon-small bg-transparent"
                            anchor-icon="icon-font i16 tc-icon-more-vert"
                            drop-down-options={getViewpointDropDownOptions(
                              viewPoint.guid
                            )}
                            on:selection-changed={(event: CustomEvent) => {
                              if (
                                event.detail.selected ===
                                translate('TC.Common.Preview')
                              ) {
                                setIsPreviewOn(true);
                                setPreviewMode('VIEW');
                                setPreviewIndex(index);
                              } else if (
                                event.detail.selected ===
                                getOpenIn360ViewerLabel()
                              ) {
                                void openIn360Viewer(viewPoint.guid);
                              } else if (
                                event.detail.selected ===
                                translate('TC.Views.LoadWithSpecificVersions')
                              ) {
                                handle3DAction(
                                  'load-viewpoint-original-versions',
                                  { viewId: viewPoint.guid }
                                );
                              }
                            }}
                          />
                        </div>
                      </li>
                    )
                  )}
                />
              </ul>
            </div>
          }
        >
          <div class="empty-state-height">
            {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
            <tc-empty
              icon="icon-font tc-icon-views"
              message={translate('TC.Views.EmptyState')}
              is-small-container={true}
              class="tc-empty-icon-large"
            />
          </div>
        </Show>
        <Show when={viewpointErrorFlag()}>
          <div class="px-1">
            <div class="alert error group">
              <div class="group-items">
                <span class="block flex-grow-1">Unable to load views</span>
              </div>
            </div>
          </div>
        </Show>
        <Show when={!isPreviewOn()}>
          {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
          <tc-fab-button
            icon="tc-icon-views-add"
            fab-class="topic-doc-fab-button"
            data-testid="issue-management-add-view-button"
            always-display={true}
            onClick={handleAddIssueView}
          />
        </Show>
      </div>
    );
  };

  // Comments Section
  const singleCommentsSection = (): JSX.Element => (
    <CommentProvider
      accessToken={props.accessToken}
      currentUser={props.currentUser}
      projectId={props.projectId}
      issueId={props.selectedIssue!.id}
      apiBaseUrl={getTcpsApiBaseUrl()}
    >
      <CommentContainer
        objectId={props.selectedIssue!.id}
        objectType="ISSUE"
        projectId={props.projectId}
        addedComment={addedComment()}
        commentsCount={handleCommentsCount}
        getCommentDetail={handleCommentDetail}
        isPreviewOn={false}
        isCommentActionAllowed={isCommentActionAllowed}
        policyToolTip={props.policyToolTip}
        user={props.currentUser}
        project={props.project}
        projectUser={props.projectUsers || []}
        contextId={props.contextId}
      />
    </CommentProvider>
  );

  // References Section
  const singleReferencesAttachments = (): JSX.Element => {
    // State management for references
    const [referenceList, setReferenceList] = createSignal<IReference[]>([]);
    const [isAccessible, _setIsAccessible] = createSignal(true);
    const [isAssigneeAccessible, _setIsAssigneeAccessible] = createSignal(true);
    const [currentEditList, setCurrentEditList] = createSignal<IReference[]>(
      []
    );
    const [showAttachmentPicker, setShowAttachmentPicker] = createSignal(false);
    const [refreshRefSection, _setRefreshRefSection] = createSignal(true);
    const [showAllReferences, setShowAllReferences] = createSignal(false);
    const [isLoading, setIsLoading] = createSignal(false);

    // Confirmation dialog state
    const [showDeleteAllConfirm, setShowDeleteAllConfirm] = createSignal(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = createSignal(false);
    const [referenceToDelete, setReferenceToDelete] = createSignal<string>('');
    const [deleteAllInProgress, setDeleteAllInProgress] = createSignal(false);
    const [deleteInProgress, setDeleteInProgress] = createSignal(false);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS6133: Reserved for future use
    const _issueId = (): string | undefined => props.selectedIssue?.id;
    const fileReferences = (): IReference[] =>
      referenceList().filter(
        (attachment: IReference) => attachment.type === 'FILE'
      );
    const isTempListEnabled = (): boolean =>
      props.viewPort === 'displayCreatePanel' ||
      props.viewPort === 'displayEditPanel';

    const docRefDropDownOptions = (): string[] => {
      const hasFileRefs = fileReferences().length > 0;
      const hasAccess = isAccessible() || isAssigneeAccessible();

      if (hasFileRefs && hasAccess) {
        return ['Download', 'Remove All'];
      }
      return ['Remove All'];
    };

    // Use reactive query for reference management
    const referencesQuery = referenceApiService.getIssueReferencesQuery(
      // eslint-disable-next-line solid/reactivity -- This function is used as a query parameter and doesn't need to be reactive
      () => props.contextId,
      // eslint-disable-next-line solid/reactivity -- This function is used as a query parameter and doesn't need to be reactive
      () => props.selectedIssue?.id
    );

    // Create mutations at component level to ensure proper reactive context
    const removeReferenceMutation =
      referenceApiService.removeReferenceMutation();

    const filterHiddenRefs = (refs: IReference[]): IReference[] => {
      const hidden = hiddenResourceIds();
      if (hidden.length === 0) return refs;
      return refs.filter(
        (ref) => !ref.resourceId || !hidden.includes(ref.resourceId)
      );
    };

    // React to query state changes
    createEffect(() => {
      setIsLoading(referencesQuery.isLoading);
      if (referencesQuery.isSuccess && referencesQuery.data) {
        const visible = filterHiddenRefs(referencesQuery.data);
        setReferenceList(visible);
        setReferenceCount(visible.length);
      } else if (referencesQuery.isError) {
        console.error('Failed to fetch references:', referencesQuery.error);
        setReferenceList([]);
        setReferenceCount(0);
      }
    });

    const addReference = async (reference: IReference): Promise<void> => {
      if (!props.selectedIssue?.id || !props.contextId) return;
      try {
        // Use the pre-created mutation
        const result = await addReferenceMutation.mutateAsync({
          contextId: props.contextId,
          issueId: props.selectedIssue.id,
          reference: reference,
        });

        // Update local state with the new reference
        setReferenceList((prev) => [...prev, result]);
        setReferenceCount((prev) => prev + 1);

        // Also update currentEditList if in create/edit mode
        if (isTempListEnabled()) {
          setCurrentEditList((prev) => [...prev, result]);
        }
      } catch (error) {
        console.error('Failed to add reference:', error);
      }
    };

    const removeReference = async (referenceId: string): Promise<void> => {
      if (!props.selectedIssue?.id || !props.contextId) return;

      try {
        // Use the pre-created mutation
        await removeReferenceMutation.mutateAsync({
          contextId: props.contextId,
          issueId: props.selectedIssue.id,
          referenceId: referenceId,
        });

        // Update local state by removing the reference
        setReferenceList((prev) =>
          prev.filter((ref) => ref.id !== referenceId)
        );
        setReferenceCount((prev) => prev - 1);

        // Also update currentEditList if in create/edit mode
        if (isTempListEnabled()) {
          setCurrentEditList((prev) =>
            prev.filter((ref) => ref.id !== referenceId)
          );
        }
      } catch (error) {
        console.error('Failed to remove reference:', error);
      }
    };

    const downloadReference = async (referenceId: string): Promise<void> => {
      const reference = referenceList().find((ref) => ref.id === referenceId);
      if (!reference || reference.type !== 'FILE' || !reference.resourceId)
        return;

      try {
        // Use the file API service to get download URL
        const fileApi = apiServices.fileApi;

        // Get the download URL using the TCPS API
        const downloadUrl = await fileApi.getFileDownloadUrl(
          reference.resourceId,
          reference.versionId
        );

        // Create a temporary link element to trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = reference.name || reference.description || 'download';
        link.target = '_blank';

        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Failed to download reference:', error);
        // You could add user notification here, e.g., toast notification
        // showError('Failed to download file. Please try again.');
      }
    };

    // References are automatically fetched through the reactive query above

    // Handle attachment actions
    const onAllAttachmentsAction = async (option: string): Promise<void> => {
      if (option === 'Download') {
        // Download all file references
        const fileRefs = fileReferences();
        for (const ref of fileRefs) {
          if (ref.id) {
            await downloadReference(ref.id);
          }
        }
      } else if (option === 'Remove All') {
        // Show confirmation dialog for removing all references
        setShowDeleteAllConfirm(true);
      }
    };

    // Handle delete all confirmation
    const handleDeleteAllConfirm = async (): Promise<void> => {
      setDeleteAllInProgress(true);
      try {
        // Remove all references
        const allRefs = referenceList();
        for (const ref of allRefs) {
          if (ref.id) {
            await removeReference(ref.id);
          }
        }
      } catch (error) {
        console.error('Failed to remove all references:', error);
      } finally {
        setDeleteAllInProgress(false);
        setShowDeleteAllConfirm(false);
      }
    };

    // Handle delete all cancel
    const handleDeleteAllCancel = (): void => {
      setShowDeleteAllConfirm(false);
    };

    // Handle reference item actions
    const onReferenceItemAction = async (
      referenceId: string,
      option: string
    ): Promise<void> => {
      if (option === 'View') {
        // Handle View action - redirect to view the file
        const reference = referenceList().find((ref) => ref.id === referenceId);
        if (reference && reference.resourceId) {
          // This mimics the doRedirect function from TopicsRightPanel for FILE type
          // TODO: Implement proper file viewing/View functionality
        }
      } else if (option === 'Download') {
        await downloadReference(referenceId);
      } else if (option === 'Remove') {
        // Show confirmation dialog for removing single reference
        setReferenceToDelete(referenceId);
        setShowDeleteConfirm(true);
      } else if (option === 'Locate in Explorer') {
        // TODO: Implement locate in explorer functionality
      }
    };

    const handleDeleteConfirmRefs = async (): Promise<void> => {
      setDeleteInProgress(true);
      try {
        const refId = referenceToDelete();
        if (refId) {
          await removeReference(refId);
        }
      } catch (error) {
        console.error('Failed to remove reference:', error);
      } finally {
        setDeleteInProgress(false);
        setShowDeleteConfirm(false);
        setReferenceToDelete('');
      }
    };

    const handleDeleteCancelRefs = (): void => {
      setShowDeleteConfirm(false);
      setReferenceToDelete('');
    };

    // Handle add attachment click
    const handleAddAttachment = (): void => {
      setShowAttachmentPicker(true);
    };

    // Handle attachment picker selection
    const handleAttachmentPickerSubmit = async (
      filesList: any
    ): Promise<void> => {
      try {
        // Process selected files and convert to IReference format
        if (filesList.type === 'FILE_FROM_CONNECT') {
          // Handle files from Trimble Connect
          const selectedFiles = Array.isArray(filesList.selectedObject)
            ? filesList.selectedObject
            : [filesList.selectedObject];

          for (const selectedFile of selectedFiles) {
            const reference: IReference = {
              description:
                selectedFile.name || selectedFile.displayName || 'Unknown File',
              type: 'FILE',
              resourceId:
                selectedFile.id ||
                selectedFile.versionId ||
                selectedFile.fileId,
              name: selectedFile.name || selectedFile.displayName,
              versionId: selectedFile.versionId,
            };

            await addReference(reference);
          }
        } else if (filesList.type === 'FILE_FROM_COMPUTER') {
          // Handle files from computer upload
          try {
            const project = await fetchProjectDetails();
            const defaultFolder = await getDefaultAttachmentFolder(project);

            const selectedFiles = Array.isArray(filesList.selectedObject)
              ? filesList.selectedObject
              : [filesList.selectedObject];

            // Configure TCPS URL for CDM.TCPSClient
            const regionData = projectRegion;
            const projectLocation = regionData.project?.location;
            const tcpsApiDomain =
              configuration.getRegionalTcpsApiDomain(projectLocation);

            // Parse the TCPS API URL to extract just the domain without protocol
            // The CDM client might be adding its own protocol, so let's try just the domain
            let baseOrigin;
            try {
              const url = new URL(tcpsApiDomain);
              // Try just the host without protocol first
              baseOrigin = url.host;
            } catch (error) {
              console.error('Failed to parse TCPS URL:', tcpsApiDomain, error);
              // Fallback: try to extract domain manually
              let cleanUrl = tcpsApiDomain;
              if (cleanUrl.includes('/tc/api/')) {
                cleanUrl = cleanUrl.split('/tc/api/')[0];
              }
              // Remove protocol if present
              baseOrigin = cleanUrl.replace(/^https?:\/\//, '');
            }

            // Create project payload for CDM.TCPSClient with base origin
            const projectPayload = {
              id: project.id,
              name: project.name,
              rootId: project.rootId,
              origin: baseOrigin, // Use just the base domain without API path
            };

            // Use CDM.TCPSClient.uploadFileContent for file upload
            const uploadPromise = CDM.TCPSClient.uploadFileContent(
              projectPayload,
              selectedFiles,
              defaultFolder.id,
              'FOLDER'
            );
            // Process upload result - callback is part of event handler scope
            uploadPromise.then(
              // eslint-disable-next-line solid/reactivity -- Promise callback is part of async event handler
              (fileEntry: ServiceResponse<FileEntry>[]) => {
                // Create references for each uploaded file
                // Process files sequentially to avoid reactivity issues
                const processFiles = async (): Promise<void> => {
                  for (const fileObject of fileEntry) {
                    try {
                      const reference: IReference = {
                        description: fileObject.data.name,
                        type: 'FILE',
                        resourceId: fileObject.data.id,
                        name: fileObject.data.name,
                        versionId: fileObject.data.versionId,
                      };

                      await addReference(reference);
                    } catch (referenceError) {
                      console.error(
                        `Failed to add reference for file ${fileObject.data.name}:`,
                        referenceError
                      );
                    }
                  }
                };
                void processFiles();
              },
              (error) => {
                console.error('Failed to upload files:', error);
                throw error;
              }
            );
          } catch (error) {
            console.error('Failed to process FILE_FROM_COMPUTER:', error);
            throw error;
          }
        } else if (filesList.type === 'LINK') {
          const reference: IReference = {
            description:
              filesList.selectedObject.displayText ||
              filesList.selectedObject.url,
            type: 'URL',
            url: filesList.selectedObject.url,
          };

          await addReference(reference);
        }

        setShowAttachmentPicker(false);
      } catch (error) {
        console.error('Failed to add attachments:', error);
      }
    };

    // Handle attachment picker close
    const handleAttachmentPickerClose = (): void => {
      setShowAttachmentPicker(false);
    };

    // LimitVisibleItems functionality
    const REFERENCE_ITEM_LIMIT = 5;
    const visibleReferences = (): IReference[] => {
      const refs = isTempListEnabled() ? currentEditList() : referenceList();
      if (showAllReferences() || refs.length <= REFERENCE_ITEM_LIMIT) {
        return refs;
      }
      return refs.slice(0, REFERENCE_ITEM_LIMIT);
    };

    const shouldShowMoreButton = (): boolean => {
      const refs = isTempListEnabled() ? currentEditList() : referenceList();
      return refs.length > REFERENCE_ITEM_LIMIT;
    };

    const hiddenReferencesCount = (): number => {
      const refs = isTempListEnabled() ? currentEditList() : referenceList();
      return Math.max(0, refs.length - REFERENCE_ITEM_LIMIT);
    };

    const toggleShowAllReferences = (): void => {
      setShowAllReferences(!showAllReferences());
    };

    // File Reference Item Component - similar to TopicsRightPanel FileReferenceItem
    const FileReferenceItem = (props: {
      reference: IReference;
      hasAccess: boolean;
      onAction(option: string): void;
      accessToken: string;
    }): JSX.Element => {
      const fileId = (): string | undefined => props.reference.resourceId;

      // Create a memo for the file ID accessor to avoid unnecessary re-queries
      const fileIdAccessor = createMemo(() => fileId());

      // Get file details using the file API service
      // Access fileIdAccessor within tracked scope - memo will be accessed in JSX
      const fileDetailsQueryMemo = createMemo(() => {
        // Access fileIdAccessor to ensure it's tracked
        fileIdAccessor();
        const query = fileApiService.getFileDetailsQuery(fileIdAccessor);
        return query;
      });

      const getDropDownOptions = (): string[] => {
        return props.hasAccess
          ? ['View', 'Download', 'Locate in Explorer', 'Remove']
          : ['View', 'Download', 'Locate in Explorer'];
      };

      const handleAction = (option: string): void => {
        props.onAction(option);
      };

      return (
        <li class="list-item group">
          <div class="group-items px-0 pointer">
            {/* File icon or thumbnail */}
            <Show
              when={
                !fileDetailsQueryMemo().isPending &&
                !fileDetailsQueryMemo().isError &&
                fileDetailsQueryMemo().data?.thumbnailUrl
              }
              fallback={
                <Show
                  when={fileDetailsQueryMemo().isPending}
                  fallback={
                    <i
                      class="icon-font i16 tc-icon-file"
                      classList={{ critical: fileDetailsQueryMemo().isError }}
                    />
                  }
                >
                  {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
                  <tc-spinner show={true} />
                </Show>
              }
            >
              {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
              <tc-auth-image
                src={fileDetailsQueryMemo().data?.thumbnailUrl}
                class="i16"
                alt={props.reference.description}
                access-token={props.accessToken}
              />
            </Show>

            {/* File details */}
            <div class="file-list-item reference-item">
              <div class="block">
                <span
                  class="text-ellipsis"
                  data-cy={
                    fileDetailsQueryMemo().data?.name ||
                    props.reference.description
                  }
                >
                  {fileDetailsQueryMemo().data?.name ||
                    props.reference.description}
                </span>
              </div>
            </div>

            {/* Dropdown menu */}
            {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
            <tc-dropdown-menu
              anchor-icon="icon-font tc-icon-more-vert"
              drop-down-options={getDropDownOptions()}
              anchor-style="margin: 0;"
              position="right"
              class="ml-auto"
              on:selection-changed={(event: CustomEvent) => {
                handleAction(event.detail.selected);
              }}
            />
          </div>
        </li>
      );
    };

    // Render individual reference item
    const renderReferenceItem = (reference: IReference): JSX.Element => {
      const hasAccess = isAccessible() || isAssigneeAccessible();
      const getDropDownOptions = (): string[] => {
        if (reference.type === 'FILE') {
          return hasAccess
            ? ['View', 'Download', 'Locate in Explorer', 'Remove']
            : ['View', 'Download', 'Locate in Explorer'];
        } else if (reference.type === 'URL') {
          return hasAccess ? ['Remove'] : ['View'];
        } else {
          // For other types (OBJECT, CLASH, etc.)
          return hasAccess ? ['Remove'] : ['View'];
        }
      };

      const handleReferenceAction = (option: string): Promise<void> => {
        return onReferenceItemAction(reference.id || '', option);
      };

      // Render FILE type references using the enhanced FileReferenceItem component
      if (reference.type === 'FILE' && reference.resourceId) {
        return (
          <FileReferenceItem
            reference={reference}
            hasAccess={hasAccess}
            onAction={handleReferenceAction}
            accessToken={props.accessToken}
          />
        );
      }

      // Render URL and other reference types (similar to TopicsRightPanel fallback)
      return (
        <li class="list-item group">
          <div class="group-items px-0 pointer">
            <i class="icon-font i16 tc-icon-link" />
            <div class="file-list-item reference-item">
              <div class="block">
                <Show
                  when={reference.type === 'URL' && reference.url}
                  fallback={
                    <span class="text-ellipsis" data-cy={reference.description}>
                      {reference.description}
                    </span>
                  }
                >
                  <a
                    class="link-secondary"
                    href={reference.url}
                    target="_blank"
                    rel="noreferrer"
                    data-cy={reference.description}
                  >
                    {reference.description}
                  </a>
                </Show>
              </div>
            </div>
            {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
            <tc-dropdown-menu
              anchor-icon="icon-font tc-icon-more-vert"
              drop-down-options={getDropDownOptions()}
              position="right"
              class="ml-auto"
              on:selection-changed={(event: CustomEvent) => {
                handleReferenceAction(event.detail.selected);
              }}
            />
          </div>
        </li>
      );
    };

    return (
      <div class="sub-section">
        <div class="reference-header">
          <h6 class="pl-2 section-header-title">
            {translate('TC.Topic.References')}
          </h6>
          <div class="flex-row ml-auto">
            <Show when={referenceList().length > 0}>
              <Show when={isTempListEnabled()}>
                {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
                <tc-icon-button
                  icon="tc-icon-content-add"
                  onClick={handleAddAttachment}
                />
              </Show>
              <Show
                when={isTempListEnabled()}
                fallback={
                  // @ts-expect-error -- tc-dropdown-menu is a web component defined at runtime
                  <tc-dropdown-menu
                    anchor-icon="icon-font tc-icon-more-vert"
                    drop-down-options={docRefDropDownOptions()}
                    position="right"
                    class="mr-0"
                    on:selection-changed={(event: CustomEvent) => {
                      onAllAttachmentsAction(event.detail.selected);
                    }}
                  />
                }
              >
                {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
                <tc-dropdown-menu
                  anchor-icon="icon-font tc-icon-more-vert"
                  drop-down-options={['Remove File']}
                  position="right"
                  class="mr-0"
                  on:selection-changed={(event: CustomEvent) => {
                    onAllAttachmentsAction(event.detail.selected);
                  }}
                />
              </Show>
            </Show>
          </div>
        </div>
        <Show
          when={referenceList().length === 0 && !isLoading()}
          fallback={
            <div class="section-details pr-0">
              <Show when={isLoading()}>
                <div class="text-center py-4">
                  {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
                  <tc-spinner show={true} />
                </div>
              </Show>
              <Show when={refreshRefSection() && !isLoading()}>
                <ul class="list">
                  {/* Render visible references with limit */}
                  <For each={visibleReferences()}>
                    {(reference) => renderReferenceItem(reference)}
                  </For>

                  {/* Show More/Show Less button */}
                  <Show when={shouldShowMoreButton()}>
                    <div class="label-group">
                      <button
                        class="button link-primary inline-left show-more"
                        type="button"
                        onClick={toggleShowAllReferences}
                        data-cy={
                          showAllReferences() ? 'show-less' : 'show-more'
                        }
                      >
                        {showAllReferences()
                          ? 'Show Less'
                          : `See More (${hiddenReferencesCount()} more)`}
                      </button>
                    </div>
                  </Show>
                </ul>
              </Show>
            </div>
          }
        >
          <div class="empty-state-height">
            <div class="text-center py-4">
              <i
                class="icon-font tc-icon-file text-gray-400 mb-2"
                // eslint-disable-next-line @typescript-eslint/naming-convention
                style={{
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  'font-size': '2.5rem',
                  width: '2.5rem',
                  height: '2.5rem',
                }}
              />
              <p class="text-gray-600">{translate('TC.Topic.NoReferences')}</p>
            </div>
          </div>
        </Show>
        <Show when={!isTempListEnabled()}>
          {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
          <tc-fab-button
            icon="tc-icon-content-add"
            fab-class="topic-doc-fab-button"
            always-display="true"
            on:fab-button-click={handleAddAttachment}
          />
        </Show>

        {/* AttachmentPicker Dialog */}
        <Show when={showAttachmentPicker()}>
          <AttachmentPicker
            addAttachment={handleAttachmentPickerSubmit}
            onClose={handleAttachmentPickerClose}
            allowedAttachmentTypes="FILE_FROM_CONNECT,FILE_FROM_COMPUTER,LINK"
            projectId={props.projectId}
            accessToken={props.accessToken}
            tcpsApiDomain={getTcpsApiBaseUrl()}
            customTitle={translate('TC.Topic.AddReferences')}
            customButtonName={translate('TC.Topic.AddReferences')}
            disableMultiSelect={false}
            projectName={props.project?.name || 'Project'}
            is3DEmbbedded={true}
            inProgressFlag={isLoading()}
          />
        </Show>

        {/* Delete All Confirmation Dialog */}
        <tc-confirm-popup
          show={showDeleteAllConfirm()}
          waiting={deleteAllInProgress()}
          title={translate('TC.Topic.DeleteReference')}
          description={translate('TC.Topic.DeleteAllReferenceWarningMsg')}
          primary-btn-label={translate('TC.Common.RemoveAll')}
          primary-btn-class="warning"
          ref={(el: HTMLElement | null) => {
            if (el) {
              // Remove any existing listeners to prevent duplicates
              el.removeEventListener(
                'confirm-popup-action',
                handleDeleteAllConfirm
              );
              el.removeEventListener(
                'confirm-popup-close',
                handleDeleteAllCancel
              );
              // Add new listeners
              el.addEventListener(
                'confirm-popup-action',
                handleDeleteAllConfirm
              );
              el.addEventListener('confirm-popup-close', handleDeleteAllCancel);
            }
          }}
        />

        {/* Delete Single Reference Confirmation Dialog */}
        <tc-confirm-popup
          show={showDeleteConfirm()}
          waiting={deleteInProgress()}
          title={translate('TC.Topic.DeleteReference')}
          description={translate('TC.Topic.DeleteReferenceWarningMsg')}
          primary-btn-label="Remove"
          primary-btn-class="warning"
          ref={(el: HTMLElement | null) => {
            if (el) {
              // Remove any existing listeners to prevent duplicates
              el.removeEventListener(
                'confirm-popup-action',
                handleDeleteConfirmRefs
              );
              el.removeEventListener(
                'confirm-popup-close',
                handleDeleteCancelRefs
              );
              // Add new listeners
              el.addEventListener(
                'confirm-popup-action',
                handleDeleteConfirmRefs
              );
              el.addEventListener(
                'confirm-popup-close',
                handleDeleteCancelRefs
              );
            }
          }}
        />
      </div>
    );
  };

  // 3D Attachments Section
  const single3dAttachments = (): JSX.Element => {
    const isLoading = (action: I3DActionType): boolean =>
      threeDActionLoading() === action;

    return (
      <>
        <div class="sub-section relative" data-testid="issue-marker-section">
          <div class="reference-header pl-2">
            <h5 class="section-header-title-no-transform">
              {translate('TC.Topic.Marker')}
            </h5>
          </div>
          <div class={`${currentMarker() ? 'pl-2' : 'px-2'}`}>
            <Show
              when={currentMarker()}
              fallback={
                <>
                  <p>{translate('TC.Topic.MarkerDescription')}</p>
                  <button
                    class="button full-width default small mb-1 mt-1"
                    data-testid="issue-marker-add-button"
                    onClick={handleAddIssueMarker}
                    disabled={isLoading('add-issue-marker')}
                  >
                    {isLoading('add-issue-marker')
                      ? translate('TC.Common.Adding') + '...'
                      : translate('TC.Topic.AddMarker')}
                  </button>
                </>
              }
            >
              {(marker) => (
                <div class="reference-header">
                  <ul class="list">
                    <li class="list-item group">
                      <div class="group-items px-0 marker-flex-row-wrap">
                        <img class="mr-1" src={markerIcon()} />
                        <span class="text-nowrap">
                          {`| X ${marker().position.x.toFixed(DECIMAL_PLACES)}`}
                        </span>
                        <span class="text-nowrap">
                          {`| Y ${marker().position.y.toFixed(DECIMAL_PLACES)}`}
                        </span>
                        <span class="text-nowrap">
                          {`| Z ${marker().position.z.toFixed(DECIMAL_PLACES)}`}
                        </span>
                      </div>
                    </li>
                  </ul>
                  {/* Marker Actions - positioned at right end */}
                  <Show when={currentMarker()}>
                    <div>
                      {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
                      <tc-dropdown-menu
                        type="icon"
                        anchor-icon="icon-font tc-icon-more-vert"
                        anchor-class-name="icon-medium tertiary icon-circle"
                        drop-down-options={['Fit to view', 'Remove']}
                        position="right"
                        data-testid="issue-marker-dropdown"
                        style={{ zIndex: 1000 }}
                        on:selection-changed={(event: CustomEvent) => {
                          if (event.detail.selected === 'Remove') {
                            handleRemoveIssueMarker();
                          } else if (event.detail.selected === 'Fit to view') {
                            handleViewIssueModelObjects();
                          }
                        }}
                      />
                    </div>
                  </Show>
                </div>
              )}
            </Show>
          </div>
        </div>

        <Show when={!isElementHidden('linked-model-objects')}>
          <div class="sub-section">
            <div class="reference-header pl-2">
              <h5
                class="section-header-title-no-transform"
                data-testid="issue-linked-objects-title"
              >
                {translate('TC.Objects.TitleWithCount').replace(
                  '{{count}}',
                  String(linkedObjectsCount())
                )}
              </h5>
              {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
              <tc-dropdown-menu
                type="icon"
                anchor-icon="icon-font tc-icon-more-vert"
                anchor-class-name="icon-medium tertiary icon-circle"
                drop-down-options={['Unlink all']}
                position="right"
                style={{ zIndex: 1000 }}
                data-testid="issue-linked-objects-dropdown"
                on:selection-changed={(event: CustomEvent) => {
                  if (event.detail.selected === 'Unlink all') {
                    handleUnlinkAllIssueModelObjects();
                  }
                }}
              />
            </div>
            <div class="section-details">
              <div>
                <button
                  class="button full-width default small mb-1"
                  onClick={handleViewIssueModelObjects}
                  disabled={isLoading('view-issue-model-objects')}
                  data-testid="issue-view-model-objects-button"
                >
                  {isLoading('view-issue-model-objects')
                    ? 'Loading...'
                    : 'View model objects'}
                </button>
              </div>
              <div class="row-distribute">
                <div class="mr-1" style={{ width: '50%' }}>
                  <button
                    class="button full-width default small mr-1"
                    onClick={handleLinkIssueModelObjects}
                    disabled={isLoading('link-issue-model-objects')}
                    data-testid="issue-link-selected-button"
                  >
                    {isLoading('link-issue-model-objects')
                      ? 'Linking...'
                      : 'Link selected'}
                  </button>
                </div>
                <div style={{ width: '50%' }}>
                  <button
                    class="button full-width default small"
                    onClick={handleUnlinkIssueModelObjects}
                    disabled={isLoading('unlink-issue-model-objects')}
                    data-testid="issue-unlink-selected-button"
                  >
                    {isLoading('unlink-issue-model-objects')
                      ? 'Unlinking...'
                      : 'Unlink selected'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Show>
      </>
    );
  };

  // Main display function similar to displaySingleView
  const displaySingleView = (): JSX.Element => (
    <>
      {singleIssueDetails()}
      <div class="sub-section">
        <div class="section-details">
          <div style={{ position: 'relative' }}>
            <Show
              when={thumbnailBlobUrl()}
              fallback={
                <div
                  style={{
                    // eslint-disable-next-line @typescript-eslint/naming-convention -- CSS property names
                    'background-color': 'rgb(108, 117, 125)',
                    height: '7.5rem',
                    display: 'flex',
                    // eslint-disable-next-line @typescript-eslint/naming-convention -- CSS property names
                    'justify-content': 'center',
                    // eslint-disable-next-line @typescript-eslint/naming-convention -- CSS property names
                    'align-items': 'center',
                  }}
                >
                  <i
                    class="icon-font tc-icon-image"
                    style={{
                      color: 'white',
                      // eslint-disable-next-line @typescript-eslint/naming-convention -- font-size is a CSS property name
                      'font-size': '24px',
                    }}
                  />
                </div>
              }
            >
              <img
                src={thumbnailBlobUrl()}
                alt=""
                style={{
                  // eslint-disable-next-line @typescript-eslint/naming-convention -- object-fit is a CSS property name
                  'object-fit': 'cover',
                  width: '100%',
                  height: '7.5rem',
                }}
              />
            </Show>
            <Show when={firstViewpointShows360Action()}>
              <img
                src={EMBEDDED_VIEW_360_BADGE_SRC}
                alt="Embedded view 360"
                style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  width: '111px',
                  height: '32px',
                  // eslint-disable-next-line @typescript-eslint/naming-convention -- CSS property names
                  'pointer-events': 'none',
                }}
              />
            </Show>
          </div>
          <div class="d-flex justify-content-between align-items-center mb-1 pt-1">
            <div class="d-flex align-items-center" style={{ gap: '4px' }}>
              {props.selectedIssue?.dueDate && (
                <>
                  <i
                    class="icon-font tc-icon-calendar"
                    style={{
                      display: 'flex',
                      // eslint-disable-next-line @typescript-eslint/naming-convention -- CSS property names
                      'align-items': 'center',
                    }}
                  />
                  <span>
                    {parseDateForDisplay(props.selectedIssue.dueDate)}
                  </span>
                </>
              )}
            </div>

            <Show
              when={
                !isElementHidden('view-3d-button') &&
                !viewsQuery.isLoading &&
                !isReservedViewDataLoading()
              }
            >
              <button
                class="button small d-flex align-items-center"
                style={`
                  background-color: #6a6976;
                  border-radius: 4px;
                  color: #fff;
                  font-weight: 500;
                  justify-content: center;
                `}
                disabled={overlayViewpointList().length === 0}
                onClick={() => {
                  const firstViewpoint = overlayViewpointList()[0];
                  if (firstViewpoint) {
                    void handleIssueViewSelect(firstViewpoint.guid);
                  }
                }}
              >
                <small class="text-bold">
                  {firstViewpointShows360Action()
                    ? getView360Label()
                    : (props.featureConfig?.customText?.view3DButtonLabel ??
                      translate('TC.Topic.View3D'))}
                </small>
                <i class="icon-font tc-icon-full-screen ml-1" />
              </button>
            </Show>
          </div>

          {(props.selectedIssue?.type || props.selectedIssue?.priority) && (
            <div class="row-distribute mt-2">
              {props.selectedIssue?.type && (
                <button
                  class="button hollow small icon-left"
                  style="cursor: default; pointer-events: none"
                >
                  <Show
                    when={selectedIssueTypeIconImageSrc()}
                    fallback={
                      <i
                        class={`icon-font ${selectedIssueTypeIconFontClass()} i16`}
                      />
                    }
                  >
                    {(src) => (
                      <div class="pr-1">
                        <img
                          class="type-icon-dimension i16"
                          src={src()}
                          alt={getTypeTranslation(
                            props.selectedIssue?.type ?? ''
                          )}
                        />
                      </div>
                    )}
                  </Show>
                  {getTypeTranslation(props.selectedIssue.type)}
                </button>
              )}
              {props.selectedIssue?.priority && (
                <button
                  class="button hollow small icon-left"
                  style={{ cursor: 'default' }}
                >
                  <i
                    class="icon-font tc-icon-bookmark i16"
                    style={{
                      color: getPriorityColor(
                        props.configurationData ?? null,
                        props.selectedIssue.priority
                      ),
                    }}
                  />
                  {getPriorityTranslation(props.selectedIssue.priority)}
                </button>
              )}
            </div>
          )}
          {/* Description */}
          {props.selectedIssue?.description &&
            props.selectedIssue.description.trim() && (
              <p class="mt-2">{props.selectedIssue.description}</p>
            )}
        </div>
      </div>
      {singleIssueCoordination()}
      <div class="sub-section">
        <div class="section-details px-0">
          <ul class="tabs flex-1">
            <li
              class={`tabs-title ${tabPort() === 'VIEW' ? 'active' : ''}`}
              onClick={() => setTabPort('VIEW')}
            >
              <i class="icon-font tc-icon-views" />
              <h5
                class="text-semibold pl-1"
                data-testid="issue-management-view-count"
              >
                {viewpointCount()}
              </h5>
            </li>
            <li
              class={`tabs-title ${tabPort() === 'COMMENT' ? 'active' : ''}`}
              onClick={() => setTabPort('COMMENT')}
            >
              <i class="icon-font tc-icon-comment" />
              <h5 class="text-semibold pl-1">{commentsCount()}</h5>
            </li>
            <Show when={!isElementHidden('references-tab')}>
              <li
                class={`tabs-title ${tabPort() === 'FILE' ? 'active' : ''}`}
                onClick={() => setTabPort('FILE')}
              >
                <i class="icon-font tc-icon-file" />
                <small class="text-semibold pl-1">{referenceCount()}</small>
              </li>
            </Show>
            <Show when={!isElementHidden('marker-tab')}>
              <li
                class={`tabs-title ${tabPort() === 'FOR3D' ? 'active' : ''}`}
                onClick={() => setTabPort('FOR3D')}
              >
                <i class="icon-font tc-icon-3D-group" />
              </li>
            </Show>
          </ul>
        </div>
      </div>
      <div class="panel-body">
        {/* Keyed Show forces re-mount of tab content when the issue changes,
            ensuring fresh data fetches for views, comments, and references */}
        <Show when={tabPort() === 'COMMENT' && props.selectedIssue?.id} keyed>
          {(_issueId) => singleCommentsSection()}
        </Show>
        <Show when={tabPort() === 'VIEW' && props.selectedIssue?.id} keyed>
          {(_issueId) => singleViewsAttachments()}
        </Show>
        <Show
          when={
            !isElementHidden('references-tab') &&
            tabPort() === 'FILE' &&
            props.selectedIssue?.id
          }
          keyed
        >
          {(_issueId) => singleReferencesAttachments()}
        </Show>
        <Show
          when={
            !isElementHidden('marker-tab') &&
            tabPort() === 'FOR3D' &&
            props.selectedIssue?.id
          }
          keyed
        >
          {(_issueId) => single3dAttachments()}
        </Show>
      </div>
    </>
  );

  // Fixed conditional rendering - use explicit boolean check
  const showPanel = (): boolean =>
    Boolean(props.isOpen) || Boolean(isCreateMode());

  return (
    <>
      <Show when={isPreviewOn()}>
        <RightPanelOverlay
          onClose={() => setIsPreviewOn(false)}
          viewpointList={overlayViewpointList()}
          previewIndex={previewIndex()}
          viewRedirection={handleIssueViewSelect}
          previewMode={previewMode()}
          projectId={props.projectId}
          issueId={props.selectedIssue?.id || ''}
          embedIn3D={false}
          deleteViewpoint={handleDeleteViewpoint}
          setViewpointIndex={(index: number) => setPreviewIndex(index)}
          accessToken={props.accessToken}
          featureConfig={props.featureConfig}
          reservedViewDataMap={reservedViewDataMap()}
          open360Viewer={openIn360Viewer}
        />
      </Show>
      <Show when={showPanel()}>
        <div class="absolute inset-0 z-dropdown">
          {/* Overlay */}
          <div
            class="absolute inset-0 bg-black bg-opacity-25"
            onClick={handleClose}
          />

          {/* Right Panel using tc-rightpanel web component */}
          <div
            class="absolute right-0 top-0 h-full w-full"
            data-cy="topics-rightpanel"
          >
            {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
            <tc-rightpanel
              header={
                !isCreateMode() && !isEditMode()
                  ? detailPanelRead()
                  : isCreateMode()
                    ? detailPanelCreateEdit('New topic')
                    : isEditMode()
                      ? detailPanelCreateEdit('Edit topic')
                      : undefined
              }
              content={
                <Show
                  when={isCreateMode() || isEditMode()}
                  fallback={displaySingleView()}
                >
                  {renderCreateForm()}
                </Show>
              }
              footer={{
                children:
                  isReadMode() && tabPort() === 'COMMENT' ? (
                    <div class="comments">
                      <Show when={!isCreateMode() && !isEditMode()}>
                        <div class="comment-components">
                          {/* Topic Comments Container - displays existing comments */}
                          {/* Add Comment Component */}
                          <CommentProvider
                            accessToken={props.accessToken}
                            currentUser={props.currentUser}
                            projectId={props.projectId}
                            issueId={props.selectedIssue!.id}
                            apiBaseUrl={getTcpsApiBaseUrl()}
                          >
                            <AddComment
                              projectId={props.projectId}
                              objectId={props.selectedIssue!.id}
                              onCommentAdded={handleCommentAdded}
                              maxHeight={200}
                              userTiduuid={props.currentUser.tiduuid}
                              embedIn3D={true}
                              refreshViewpointFn={noOpRefreshViewpointFn}
                              objectType="ISSUE"
                              contextId={props.contextId}
                              isEditMode={false}
                              shouldEmitData={false}
                              onAddIssueCommentView={handleAddIssueCommentView}
                              onCreateCommentView={handleCreateCommentView}
                            />
                          </CommentProvider>
                        </div>
                      </Show>
                    </div>
                  ) : isEditMode() || isCreateMode() ? (
                    <div class="wrap-buttons px-2 py-2 mx-0">
                      <button
                        class="button link-secondary"
                        data-cy="topic-cancel"
                        onClick={() => handleCancelEdit()}
                      >
                        Cancel
                      </button>
                      <button
                        class="button primary"
                        data-cy="topic-save"
                        disabled={!issueForm.state.canSubmit}
                        onClick={() => issueForm.handleSubmit()}
                      >
                        Save
                      </button>
                    </div>
                  ) : undefined,
              }}
            />

            {/* Export Dialog - Positioned as overlay within the panel */}
            <Show when={showExportDialog()}>
              <IssueExport
                show={showExportDialog()}
                onClose={handleCloseExportDialog}
                projectId={props.projectId}
                filteredIssueList={
                  props.selectedIssue ? [props.selectedIssue] : []
                }
                selectedRows={[0]}
                exportMode="SELECTED"
                searchItems={searchItems()}
                setSearchItems={setSearchItems}
                isAssignedToMe={isAssignedToMe()}
                toggleAssignedToMe={handleToggleAssignedToMe}
                filterData={filterData()}
                setFilterData={setFilterData}
                onFilter={handleExportFilter}
                currentUserEmail={props.currentUser.email}
                embedIn3D={false}
                setWidgetMode={props.setWidgetMode}
                clearExports={props.clearExports}
                addExports={props.addExports}
              />
            </Show>

            {/* Sharing Modal - tc-rightpanel renders its content via a custom element prop,
                so the share button is detached from the document and cannot be clicked in tests
                via SolidJS event delegation. The modal uses show/show=false via its internal
                createEffect to load/reset state, so wrapping in <Show> is not needed here. */}
            <IssueSharingModal
              show={showSharingModal()}
              issueIds={props.selectedIssue ? [props.selectedIssue.id] : []}
              contextId={props.contextId ?? ''}
              onClose={handleSharingModalClose}
              onSave={handleSharingModalSave}
              projectUsersData={props.projectUsersData ?? []}
              allAssignees={props.allAssignees}
              canSave={canUpdatePermissions()}
              creatorIdentity={props.selectedIssue?.createdBy}
            />

            {/* Delete Confirmation Dialog */}
            <tc-confirm-popup
              show={showDeleteConfirm()}
              waiting={deleteInProgress()}
              title={translate('TC.Topic.DeleteTopic')}
              description={translate('TC.Topic.DeleteTopicWarningMsg')}
              primary-btn-label={
                deleteInProgress()
                  ? translate('TC.Common.Deleting.Dotless') + '...'
                  : translate('TC.Common.Delete')
              }
              primary-btn-class="warning"
              secondary-btn-label={translate('TC.Common.Cancel')}
              secondary-btn-class="link-secondary"
              ref={(el: HTMLElement | null) => {
                if (el) {
                  // Remove any existing listeners to prevent duplicates
                  el.removeEventListener(
                    'confirm-popup-action',
                    handleDeleteConfirm
                  );
                  el.removeEventListener(
                    'confirm-popup-close',
                    handleDeleteCancel
                  );
                  // Add new listeners
                  el.addEventListener(
                    'confirm-popup-action',
                    handleDeleteConfirm
                  );
                  el.addEventListener(
                    'confirm-popup-close',
                    handleDeleteCancel
                  );
                }
              }}
            />
          </div>
        </div>
      </Show>
    </>
  );
}
